import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { API_URLS, GCS_BUCKET_URL } from '../config/api';
import { apiGet, apiPost, apiPut, apiDelete, showToast } from '../utils/apiUtils';
import { getToken, getAnonToken } from '../utils/authUtils';
import { BRAND, HAS_PAYMENTS, HAS_TELEGRAM, METRIKA_ID, localePath } from '../config/region';
import { trackEvent, trackSessionMessage } from '../utils/analytics';import { t } from '../i18n';
import AuthModal from './AuthModal';
import ParticleBackground from './ParticleBackground';
import ReportModal from './ReportModal';
import { Menu, ScrollText, Eye, Bot, ImageOff, Image, Info, Lightbulb, X } from 'lucide-react';
import '../App.css';
import './PlayPage.css';

// Shown only until GET /image-models answers. Names, prices and copy all live in
// IMAGE_MODEL_CATALOG on the backend — the same table the turn is billed from — so this array is a
// placeholder for the first paint, never the source of truth.
// Must match DEFAULT_IMAGE_MODEL in backend/src/utils/image_generation.py: the backend falls back
// to it for any open world whose saved image_style predates the per-model setting, and the picker
// has to show the model that will actually be billed.
const DEFAULT_IMAGE_MODEL = 'sirius';

const IMAGE_MODEL_FALLBACK = [
  { id: 'sirius', name: t('Сириус'), stars: 50, description: '' },
  { id: 'aurora', name: t('Аврора'), stars: 80, description: '' },
  { id: 'vega', name: t('Вега'), stars: 120, description: '' },
];

const PlayPage = () => {
  const { playLink } = useParams();

  // Game state
  const [gameState, setGameState] = useState({
    novel_id: null,
    game_state_id: null,
    character_name: null,
    character_message: null,
    sprites: [],
    background: null,
    music: null,
    choices: [],
    help: '',
    interact: false,
    biography_needed: false,
    static_endings: [],
    payment_required: false,
    payment_string: null,
    payment_price: null,
    telegram_required: false,
    telegram_verification_code: null,
    telegram_group_link: null,
    telegram_bot_name: null,
    telegram_bot_link: null,
    preferred_biography: null,
    required_18_age: false,
    current_messages_count: 0,
    max_messages_count: null,
    is_open_world: false
  });

  // User input state
  const [userMessage, setUserMessage] = useState('');
  const [userCharacterName, setUserCharacterName] = useState('');
  const [userCharacterBiography, setUserCharacterBiography] = useState('');
  const [customBiography, setCustomBiography] = useState('');
  const [showCustomBiography, setShowCustomBiography] = useState(false);
  const [userGender, setUserGender] = useState('');
  const [ageVerified, setAgeVerified] = useState(false);
  const [messageHistory, setMessageHistory] = useState([]);

  // UI state
  const [loading, setLoading] = useState(true);
  const [musicPlaying, setMusicPlaying] = useState(() => {
    const saved = localStorage.getItem('pp-music-playing');
    return saved !== null ? saved === 'true' : true;
  });
  const [audioRef, setAudioRef] = useState(null);
  const [currentMusic, setCurrentMusic] = useState(null);
  const [musicPlaylist, setMusicPlaylist] = useState([]);
  const [currentTrackIndex, setCurrentTrackIndex] = useState(0);
  const [shuffledPlaylist, setShuffledPlaylist] = useState([]);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [rollbackConfirmId, setRollbackConfirmId] = useState(null);
  const isFadingRef = useRef(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [maxVisibleChars, setMaxVisibleChars] = useState(() => {
    const saved = localStorage.getItem('pp-max-visible-chars');
    return saved ? parseInt(saved, 10) : 2;
  });

  // Fatal error state
  const [fatalError, setFatalError] = useState(false);

  // Payment state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalStarMode, setAuthModalStarMode] = useState(false);

  // Saves state
  const [savesModalOpen, setSavesModalOpen] = useState(false);
  const [gameSaves, setGameSaves] = useState([]);
  const [loadingSaves, setLoadingSaves] = useState(false);
  const [newSaveName, setNewSaveName] = useState('');
  const [copiedStatus, setCopiedStatus] = useState('');
  const [isIOSFullscreen, setIsIOSFullscreen] = useState(false);
  const [hintHidden, setHintHidden] = useState(() => {
    return parseInt(localStorage.getItem('pp-continue-tap-count') || '0', 10) >= 10;
  });

  // Dynamic sprite positioning

  // Menu modal state
  const [menuModalOpen, setMenuModalOpen] = useState(false);
  const [uiHidden, setUiHidden] = useState(false);
  const [spritesHidden, setSpritesHidden] = useState(false);
  const [chatMode, setChatMode] = useState(false);
  const [choicesHidden, setChoicesHidden] = useState(false);
  const [choiceClickCount, setChoiceClickCount] = useState(() => parseInt(localStorage.getItem('pp-choice-clicks') || '0', 10));

  // Message chunk navigation (mobile only)
  const [msgChunkIndex, setMsgChunkIndex] = useState(0);
  const [chunkDragX, setChunkDragX] = useState(0); // px offset while dragging
  const [chunkDragging, setChunkDragging] = useState(false);
  const [chunkEnterDir, setChunkEnterDir] = useState(null); // 'left' | 'right' after release
  const prevCharMsgRef = useRef(null);
  const dragStartRef = useRef(null);
  const msgChunksRef = useRef([]);
  const msgContentRef = useRef(null);
  const chunkEnterTimeout = useRef(null);
  const handleSendMessageRef = useRef(null);

  // Reply suggestions (lightbulb button next to the input)
  const [suggestions, setSuggestions] = useState(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);


  const [tappedAvatarId, setTappedAvatarId] = useState(null);
  const tappedAvatarTimer = useRef(null);

  // AI Models state
  const [aiModels, setAiModels] = useState([]);
  const [selectedAiModel, setSelectedAiModel] = useState(() => {
    const savedModel = localStorage.getItem('playpage-selected-ai-model');
    return savedModel || 'Принцесса';
  });
  const [aiModelSelectorOpen, setAiModelSelectorOpen] = useState(false);
  const [pendingTestModel, setPendingTestModel] = useState(null);
  // "Reload dialogue" marker: the next message starts the AI's context from scratch
  const [resetDialogue, setResetDialogue] = useState(false);
  // Extended summary: bigger memory and dialogue window, model price x2
  const [extendedSummary, setExtendedSummary] = useState(() => localStorage.getItem('playpage-extended-summary') === 'true');
  const toggleExtendedSummary = (value) => {
    setExtendedSummary(value);
    localStorage.setItem('playpage-extended-summary', value);
  };

  // Player's style note for AI responses — saved on the user, applied to every novel
  const [aiPreferenceOpen, setAiPreferenceOpen] = useState(false);
  const [aiPreference, setAiPreference] = useState('');
  const [aiPreferenceSaving, setAiPreferenceSaving] = useState(false);

  // Player's own lore for this novel — saved on the user's novel game, appended to the system prompt
  const [ownLoreOpen, setOwnLoreOpen] = useState(false);
  const [ownLore, setOwnLore] = useState('');
  const [ownLoreSaving, setOwnLoreSaving] = useState(false);
  const [ownLoreLoading, setOwnLoreLoading] = useState(false);

  // Sprite positioning: top = max(msgBox bottom, threshold), bottom = screen bottom
  const initialViewportHeight = useRef(window.innerHeight);
  const [bgHeight, setBgHeight] = useState(window.innerHeight);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  // Single source of truth for "is this a phone/tablet". Keep in sync with
  // isMobileViewport() below, used by listeners that can't read state.
  const isMobileDevice = screenWidth <= 768 || bgHeight <= 500;
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const prevSpritesRef = useRef(null);

  // Refs
  const messageInputRef = useRef(null);
  const messageContainerRef = useRef(null);
  const bottomHeaderRef = useRef(null);
  const randomNonSpeakerRef = useRef(null);
  const prevSpriteIdsRef = useRef(null);
  const historyMessagesRef = useRef(null);
  const pauseTimeoutRef = useRef(null);
  const chatMessagesRef = useRef(null);
  // Kept in sync below so the bfcache listener can read the latest values without
  // re-subscribing on every render.
  const fetchGameStateRef = useRef(null);
  // A restore that arrived while a turn was in flight: 'reload' or 'refetch', run once it settles.
  const pendingRestoreRef = useRef(null);
  const loadingRef = useRef(true);

  // State for star balance modal
  const [starBalanceModalOpen, setStarBalanceModalOpen] = useState(false);
  const [csamBlockedModal, setCsamBlockedModal] = useState({ open: false, counter: 0 });

  // Report state
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [endOverlayClosed, setEndOverlayClosed] = useState(false);
  const [owDisclaimerShown, setOwDisclaimerShown] = useState(false);
  const [owImageGenEnabled, setOwImageGenEnabled] = useState(true);
  const [owImageStyle, setOwImageStyle] = useState('anime');
  const [owImageModel, setOwImageModel] = useState(DEFAULT_IMAGE_MODEL);
  const [imageModels, setImageModels] = useState(IMAGE_MODEL_FALLBACK);
  const [owInfoModalOpen, setOwInfoModalOpen] = useState(false);
  const [owInfo, setOwInfo] = useState(null);
  const [owInfoLoading, setOwInfoLoading] = useState(false);
  const [owSavingStyle, setOwSavingStyle] = useState(false);
  const [safariFocused, setSafariFocused] = useState(false);

  // Helper function to get gender text
  const getGenderText = () => {
    if (!userGender) return '';
    return userGender === 'male'
      ? 'Пол: мужской. Парень'
      : 'Пол: женский. Девушка ';
  };

  const updateBiography = (genderText, customText) => {
    const parts = [genderText, customText].filter(part => part && part.trim());
    setUserCharacterBiography(parts.join(' '));
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const formatMessage = (text) => {
    if (!text) return text;
    let key = 0;
    const formatAsterisks = (str, baseStyle) => {
      const parts = str.split(/(\*[^*]*\*)/g);
      return parts.map(p => {
        if (p.startsWith('*') && p.endsWith('*')) {
          return <span key={key++} style={{ ...baseStyle, opacity: 0.6, fontWeight: 'normal' }}>{p.slice(1, -1)}</span>;
        }
        return <span key={key++} style={baseStyle}>{p}</span>;
      });
    };
    const parts = text.split(/("[^"]*")/g);
    return parts.map(part => {
      if (part.startsWith('"') && part.endsWith('"')) {
        const inner = part.slice(1, -1);
        return [<span key={key++} style={{ fontWeight: 'bold' }}>"</span>, ...formatAsterisks(inner, { fontWeight: 'bold' }), <span key={key++} style={{ fontWeight: 'bold' }}>"</span>];
      }
      return formatAsterisks(part, { fontWeight: 'bold' });
    });
  };

  // Chars per page on desktop. Desktop pages purely by length — no sentence grouping.
  // 1.5x the old 1200: a desktop message box fits far more than a phone's, so the shorter
  // pages meant flipping through a reply that would have fitted on screen in one go.
  const DESKTOP_CHUNK_CHARS = 1800;

  // Cut a string into <= limit-char pieces at word boundaries, keeping *italic*
  // markers balanced across the cut.
  const splitByCharLimit = (str, limit) => {
    const out = [];
    let rest = str;
    while (rest) {
      if (rest.length <= limit) { out.push(rest); break; }
      const cutoff = rest.indexOf(' ', limit);
      if (cutoff === -1) { out.push(rest); break; }
      const cutChunk = rest.slice(0, cutoff);
      const remainder = rest.slice(cutoff + 1).trim();
      if ((cutChunk.match(/\*/g) || []).length % 2 === 1) {
        out.push(cutChunk + '*');
        rest = '*' + remainder;
      } else {
        out.push(cutChunk);
        rest = remainder;
      }
    }
    return out;
  };

  // Merge a too-short trailing chunk (e.g. a lone ".") back into the previous one
  const mergeShortTail = (result) => {
    if (result.length > 1 && result[result.length - 1].replace(/[*\s]/g, '').length < 25) {
      const last = result.pop();
      result[result.length - 1] += ' ' + last;
    }
    return result;
  };

  // Window-based twin of isMobileDevice, for listeners that can't read state.
  const isMobileViewport = () => window.innerWidth <= 768 || window.innerHeight <= 500;

  // Split message into chunks by *narrative* blocks
  const splitMessageChunks = (text) => {
    if (!text) return [];
    if (!isMobileDevice) {
      const desktopChunks = mergeShortTail(splitByCharLimit(text, DESKTOP_CHUNK_CHARS));
      return desktopChunks.length > 0 ? desktopChunks : [text];
    }

    const isPortrait = bgHeight > screenWidth;
    const groupSize = isPortrait ? 3 : 4;
    const charLimit = 400;

    const splitBySentences = (str) => {
      const sentences = str.split(/(?<=[.!?…»"])\s+/g).filter(s => s.trim());
      return sentences.length > 0 ? sentences : [str];
    };

    // Parse into raw sentences, preserving *italic* markers
    const parts = text.split(/(\*[^*]+\*)/g).filter(p => p.trim());
    const rawSentences = [];
    let currentSpeech = '';
    for (const part of parts) {
      if (part.startsWith('*') && part.endsWith('*')) {
        if (currentSpeech.trim()) {
          splitBySentences(currentSpeech.trim()).forEach(s => rawSentences.push(s));
          currentSpeech = '';
        }
        splitBySentences(part.slice(1, -1)).forEach(s => rawSentences.push(`*${s}*`));
      } else {
        currentSpeech += part;
      }
    }
    if (currentSpeech.trim()) splitBySentences(currentSpeech.trim()).forEach(s => rawSentences.push(s));

    // Merge short sentences (< 25 chars) with the next for counting
    const counted = [];
    for (const s of rawSentences) {
      if (counted.length > 0 && counted[counted.length - 1].length < 25) {
        counted[counted.length - 1] += ' ' + s;
      } else {
        counted.push(s);
      }
    }

    // Group by groupSize counted sentences
    const groups = [];
    for (let i = 0; i < counted.length; i += groupSize) {
      groups.push(counted.slice(i, i + groupSize).join(' '));
    }

    // Apply char limit; remainder carries forward and prepends to next group
    const result = [];
    let carry = '';
    for (const group of groups) {
      const chunk = carry ? carry + ' ' + group : group;
      carry = '';
      if (chunk.length <= charLimit) {
        result.push(chunk);
      } else {
        const cutoff = chunk.indexOf(' ', charLimit);
        if (cutoff === -1) {
          result.push(chunk);
        } else {
          const cutChunk = chunk.slice(0, cutoff);
          const remainder = chunk.slice(cutoff + 1).trim();
          const asterisksInCut = (cutChunk.match(/\*/g) || []).length;
          if (asterisksInCut % 2 === 1) {
            result.push(cutChunk + '*');
            carry = '*' + remainder;
          } else {
            result.push(cutChunk);
            carry = remainder;
          }
        }
      }
    }
    if (carry) splitByCharLimit(carry, charLimit).forEach(c => result.push(c));

    mergeShortTail(result);

    return result.length > 0 ? result : [text];
  };

  const getAiModelEmoji = (model) => model.emoji || '🤖';

  const getSanitizedErrorMessage = (error) => {
    const defaultErrorMessage = t('Произошла ошибка. Попробуйте снова или позже.');
    if (error === defaultErrorMessage) return error;
    let errorMessage = '';
    if (typeof error === 'string') { errorMessage = error; }
    else if (error?.message) { errorMessage = error.message; }
    else if (error?.detail) { errorMessage = error.detail; }
    else { return defaultErrorMessage; }
    const allowedErrors = ['Недостаточно звёзд', 'Insufficient stars', 'Daily interaction limit exceeded', 'Дневной лимит взаимодействий', 'возвращайтесь завтра', 'return tomorrow', 'сменить модель'];
    const isAllowedError = allowedErrors.some(allowed => errorMessage.toLowerCase().includes(allowed.toLowerCase()));
    if (isAllowedError) return errorMessage;
    return defaultErrorMessage;
  };

  const handleStarBalanceError = (errorMessage, gameStateData = null) => {
    const hasStarBalanceError = errorMessage.includes('Недостаточно звёзд') || errorMessage.includes('Insufficient stars');
    const minCost = 10;
    const hasLowStarBalance = gameStateData && gameStateData.user_star_balance !== undefined && gameStateData.user_star_balance < minCost;
    if (hasStarBalanceError || hasLowStarBalance) {
      const token = getToken();
      if (!token) { setAuthModalStarMode(true); setShowAuthModal(true); }
      else { setStarBalanceModalOpen(true); }
      return true;
    }
    return false;
  };

  const incrementTapCount = useCallback(() => {
    if (hintHidden) return;
    const count = parseInt(localStorage.getItem('pp-continue-tap-count') || '0', 10) + 1;
    localStorage.setItem('pp-continue-tap-count', String(count));
    if (count >= 10) setHintHidden(true);
  }, [hintHidden]);

  const getHoursUntilNextDay = () => {
    const now = new Date();
    const tomorrow = new Date();
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    tomorrow.setUTCHours(0, 0, 0, 0);
    return Math.ceil((tomorrow - now) / (1000 * 60 * 60));
  };

  // Load game state on component mount
  useEffect(() => { fetchGameState(); fetchAiModels(); }, [playLink]);

  useEffect(() => { fetchGameStateRef.current = fetchGameState; });
  useEffect(() => {
    loadingRef.current = loading;
    // Deferred restore (see below): the turn has settled — saved, or failed — so the page can
    // resync with the DB without throwing away an answer that was still arriving.
    if (loading || !pendingRestoreRef.current) return;
    const pending = pendingRestoreRef.current;
    pendingRestoreRef.current = null;
    if (pending === 'reload') window.location.reload();
    else fetchGameStateRef.current?.();
  }, [loading]);

  // Leaving the page (payment, ads) and coming back with the browser's Back button
  // hands the player the view cached in that history entry — the turn they were on
  // when the entry was created, not the one they left off at. Playing forward never
  // pushes a history entry, so that snapshot is usually the very start of the run.
  // A refetch alone is not enough: the restore also thaws the old React state, and
  // any turn sent from it branches off an already-played state, which reads as the
  // story jumping backwards with stars spent on it. Reloading is the only way to
  // guarantee the page and the DB agree.
  //
  // Three restore paths have to be covered, because browsers pick between them:
  //   - pageshow.persisted   — bfcache restore, heap thawed, no remount
  //   - navigation type      — history entry re-fetched instead of bfcached
  //   - visibilitychange     — mobile froze the tab without firing pageshow
  //
  // None of them may fire while a turn is in flight. The backend saves a turn only once its answer
  // has finished, so reloading mid-answer handed back the turn before it — the story jumped back a
  // message or two, into the previous location after a scene change, with the half-seen answer
  // repeated. A restore that lands mid-turn is deferred until loading settles (effect above).
  useEffect(() => {
    const reload = () => {
      if (loadingRef.current) { pendingRestoreRef.current = 'reload'; return; }
      window.location.reload();
    };

    const handlePageShow = (event) => { if (event.persisted) reload(); };

    // Covers a back/forward navigation that rebuilt the document from the HTTP
    // cache rather than the bfcache. The mount fetch below is already in flight,
    // but the restored markup can still show the cached view, so force a fresh load.
    const navEntry = performance.getEntriesByType?.('navigation')?.[0];
    if (navEntry?.type === 'back_forward') reload();

    // Mobile Safari/Chrome may freeze and thaw the tab without a pageshow event.
    // Only a long hide is treated as a restore, so ordinary tab switching and the
    // in-app ad overlay do not reload the page mid-run.
    let hiddenAt = null;
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') { hiddenAt = Date.now(); return; }
      if (hiddenAt && Date.now() - hiddenAt > 30000) {
        hiddenAt = null;
        if (loadingRef.current) {
          // A pending full reload already covers a refetch.
          if (!pendingRestoreRef.current) pendingRestoreRef.current = 'refetch';
          return;
        }
        fetchGameStateRef.current?.();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Validate selected AI model when models are loaded
  useEffect(() => {
    if (aiModels.length > 0) {
      const modelExists = aiModels.some(model => model.id === selectedAiModel);
      if (!modelExists) {
        const defaultModel = 'Принцесса';
        setSelectedAiModel(defaultModel);
        localStorage.setItem('playpage-selected-ai-model', defaultModel);
      }
    }
  }, [aiModels, selectedAiModel]);

  // Chunk drag handlers — text follows finger
  const handleChunkTouchStart = useCallback((e) => {
    const chunks = msgChunksRef.current;
    if (chunks.length <= 1) return;
    dragStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, locked: false };
    setChunkDragging(true);
  }, []);

  const handleChunkTouchMove = useCallback((e) => {
    if (!dragStartRef.current) return;
    const dx = e.touches[0].clientX - dragStartRef.current.x;
    const dy = e.touches[0].clientY - dragStartRef.current.y;
    // Lock direction on first significant move
    if (!dragStartRef.current.locked) {
      if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return;
      if (Math.abs(dy) > Math.abs(dx)) { dragStartRef.current = null; setChunkDragging(false); setChunkDragX(0); return; }
      dragStartRef.current.locked = true;
    }
    e.preventDefault();
    setChunkDragX(dx);
  }, []);

  const triggerChunkEnter = useCallback((dir) => {
    setChunkEnterDir(dir);
    if (chunkEnterTimeout.current) clearTimeout(chunkEnterTimeout.current);
    chunkEnterTimeout.current = setTimeout(() => setChunkEnterDir(null), 300);
  }, []);

  const nextChunk = useCallback(() => {
    const chunks = msgChunksRef.current;
    if (chunks.length <= 1) return;
    incrementTapCount();
    setMsgChunkIndex(prev => { const n = Math.min(prev + 1, chunks.length - 1); if (n !== prev) triggerChunkEnter('right'); return n; });
  }, [triggerChunkEnter]);

  const prevChunk = useCallback(() => {
    const chunks = msgChunksRef.current;
    if (chunks.length <= 1) return;
    setMsgChunkIndex(prev => { const n = Math.max(prev - 1, 0); if (n !== prev) triggerChunkEnter('left'); return n; });
  }, [triggerChunkEnter]);

  const handleChunkTouchEnd = useCallback(() => {
    if (!dragStartRef.current) { setChunkDragging(false); setChunkDragX(0); return; }
    const dx = chunkDragX;
    const threshold = 50;
    dragStartRef.current = null;
    setChunkDragging(false);
    setChunkDragX(0);
    if (dx < -threshold) {
      nextChunk();
    } else if (dx > threshold) {
      prevChunk();
    }
  }, [chunkDragX, nextChunk, prevChunk]);

  const handleScreenClick = useCallback(() => {
    const chunks = msgChunksRef.current;
    if (chunks.length > 1 && msgChunkIndex < chunks.length - 1) {
      nextChunk();
    }
  }, [nextChunk, msgChunkIndex]);

  const handleStaticScreenClick = useCallback(() => {
    const chunks = msgChunksRef.current;
    if (chunks.length > 1 && msgChunkIndex < chunks.length - 1) {
      nextChunk();
      return;
    }
    if (gameState.interact) return;
    incrementTapCount();
    if (handleSendMessageRef.current) handleSendMessageRef.current();
  }, [nextChunk, msgChunkIndex, gameState.interact]);

  const handleMsgClick = useCallback((e) => {
    const chunks = msgChunksRef.current;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    if (clickX < rect.width / 2) {
      if (chunks.length > 1 && msgChunkIndex > 0) prevChunk();
    } else {
      if (chunks.length > 1 && msgChunkIndex < chunks.length - 1) {
        nextChunk();
      } else {
        handleStaticScreenClick();
      }
    }
  }, [nextChunk, prevChunk, msgChunkIndex, handleStaticScreenClick]);

  // Auto-scroll chat mode messages when history changes (not during streaming chunks)
  useEffect(() => {
    if (chatMode && chatMessagesRef.current) {
      setTimeout(() => {
        if (chatMessagesRef.current) chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
      }, 50);
    }
  }, [chatMode, messageHistory]);

  // Auto-scroll history to bottom when opened or messages change
  useEffect(() => {
    if (historyModalOpen) {
      setTimeout(() => {
        if (historyMessagesRef.current) {
          historyMessagesRef.current.scrollTop = historyMessagesRef.current.scrollHeight;
        }
        // Also scroll modal body (for desktop where list has no max-height)
        const modalBody = historyMessagesRef.current?.closest('.pp-modal-body');
        if (modalBody) {
          modalBody.scrollTop = modalBody.scrollHeight;
        }
      }, 50);
    }
  }, [historyModalOpen, gameHistory]);

  // Initialize states on mount
  useEffect(() => {
    setCustomBiography(''); setShowCustomBiography(false);
    setUserCharacterBiography(''); setUserGender('');
  }, []);


  // Fade sprites on change: compare previous vs new, fade out then fade in
  const [displaySprites, setDisplaySprites] = useState([]);
  const [spritesFading, setSpritesFading] = useState(false);
  const spriteFadeTimer = useRef(null);

  const updateSpritesWithFade = useCallback((newSprites) => {
    const currentKey = (newSprites || []).map(s => `${s.character_id}:${s.id}`).sort().join('|');
    const prevKey = prevSpritesRef.current;

    if (prevKey === null) {
      setDisplaySprites(newSprites || []);
      prevSpritesRef.current = currentKey;
      return;
    }

    if (currentKey === prevKey) return;

    if (spriteFadeTimer.current) clearTimeout(spriteFadeTimer.current);

    // Phase 1: fade out old sprites
    setSpritesFading(true);

    // Phase 2: after fade-out completes, swap sprites and fade in
    spriteFadeTimer.current = setTimeout(() => {
      setDisplaySprites(newSprites || []);
      prevSpritesRef.current = currentKey;
      setSpritesFading(false);
    }, 400);
  }, []);

  // Update background height on resize (including virtual keyboard)
  useEffect(() => {
    const handleResize = () => {
      initialViewportHeight.current = window.innerHeight;
      setBgHeight(window.innerHeight);
      setScreenWidth(window.innerWidth);
    };
    const handleFocusIn = (e) => {
      if (isMobileViewport() && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') && !e.target.readOnly && !e.target.disabled) setKeyboardOpen(true);
    };
    const handleFocusOut = (e) => {
      if ((e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') && !e.target.readOnly && !e.target.disabled) setKeyboardOpen(false);
    };
    window.addEventListener('resize', handleResize);
    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);



  // Auto-focus input on desktop when it becomes available
  useEffect(() => {
    if (!isMobileDevice && gameState.interact && !loading && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [loading, gameState.interact, isMobileDevice]);




  // Handle page visibility for audio
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (audioRef) {
        if (document.hidden || document.visibilityState === 'hidden') {
          if (pauseTimeoutRef.current) { clearTimeout(pauseTimeoutRef.current); pauseTimeoutRef.current = null; }
          pauseTimeoutRef.current = setTimeout(() => {
            if (audioRef && !audioRef.paused && (document.hidden || document.visibilityState === 'hidden')) audioRef.pause();
            pauseTimeoutRef.current = null;
          }, 300);
        } else if (document.visibilityState === 'visible') {
          if (pauseTimeoutRef.current) { clearTimeout(pauseTimeoutRef.current); pauseTimeoutRef.current = null; }
          if (musicPlaying && audioRef.paused) audioRef.play().catch(() => {});
        }
      }
    };
    const handlePageHide = () => { if (audioRef && !audioRef.paused) audioRef.pause(); };
    const handlePageShow = () => { if (audioRef && musicPlaying && audioRef.paused) audioRef.play().catch(() => {}); };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [audioRef, musicPlaying]);

  // Start playing when currentMusic changes
  useEffect(() => {
    if (currentMusic && audioRef && musicPlaying) {
      audioRef.load();
      setTimeout(() => {
        if (audioRef && musicPlaying) {
          if (isFadingRef.current) {
            isFadingRef.current = false;
            audioRef.volume = 0;
            audioRef.play().then(() => {
              const fadeInInterval = setInterval(() => {
                audioRef.volume = Math.min(audioRef.volume + 0.1, 1);
                if (audioRef.volume >= 1) clearInterval(fadeInInterval);
              }, 100);
            }).catch(() => { audioRef.volume = 1; });
          } else {
            audioRef.play().catch(() => {});
          }
        }
      }, 100);
    }
  }, [currentMusic, audioRef, musicPlaying]);

  // Process music playlist
  const processMusic = (newMusicList) => {
    if (!newMusicList || !Array.isArray(newMusicList) || newMusicList.length === 0) {
      if (currentMusic) {
        if (audioRef) { audioRef.pause(); audioRef.currentTime = 0; }
        setCurrentMusic(null); setMusicPlaylist([]); setShuffledPlaylist([]); setCurrentTrackIndex(0);
      }
      return;
    }
    const currentPlaylistIds = musicPlaylist.map(track => track.id);
    const newPlaylistIds = newMusicList.map(track => track.id);
    const playlistChanged = currentPlaylistIds.length !== newPlaylistIds.length || !currentPlaylistIds.every((id, index) => id === newPlaylistIds[index]);
    if (playlistChanged) {
      setMusicPlaylist(newMusicList);
      const shuffled = shuffleArray(newMusicList);
      setShuffledPlaylist(shuffled); setCurrentTrackIndex(0);
      if (audioRef && !audioRef.paused) { fadeOutAndChangeTrack(shuffled[0]); }
      else { setCurrentMusic(shuffled[0]); }
    }
  };

  const fadeOutAndChangeTrack = (newTrack) => {
    if (!audioRef) { setCurrentMusic(newTrack); return; }
    isFadingRef.current = true;
    let currentVolume = audioRef.volume;
    const fadeOutInterval = setInterval(() => {
      currentVolume -= 0.1;
      if (currentVolume <= 0) { audioRef.volume = 0; audioRef.pause(); audioRef.currentTime = 0; clearInterval(fadeOutInterval); setCurrentMusic(newTrack); }
      else { audioRef.volume = currentVolume; }
    }, 100);
  };

  const handleTrackEnd = () => {
    if (shuffledPlaylist.length === 0) return;
    if (audioRef) { audioRef.pause(); audioRef.currentTime = 0; }
    const nextIndex = (currentTrackIndex + 1) % shuffledPlaylist.length;
    setCurrentTrackIndex(nextIndex); setCurrentMusic(shuffledPlaylist[nextIndex]);
  };

  const handleAudioRef = (audio) => {
    setAudioRef(audio);
    if (audio) audio.volume = 1;
  };

  const toggleMusic = () => {
    if (audioRef) {
      if (musicPlaying) audioRef.pause(); else audioRef.play();
      const next = !musicPlaying;
      setMusicPlaying(next);
      localStorage.setItem('pp-music-playing', next);
    }
  };



  const isSafari = () => {
    const ua = navigator.userAgent;
    return /^((?!chrome|android|crios|fxios|edg).)*safari/i.test(ua);
  };

  const isIOS = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug_ios') === 'true') return true;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  };

  const toggleFullscreen = () => {
    if (isIOS()) {
      const viewport = document.querySelector('meta[name=viewport]');
      const body = document.body;
      if (body.classList.contains('ios-fullscreen')) {
        body.classList.remove('ios-fullscreen'); setIsIOSFullscreen(false);
        if (viewport) viewport.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover');
        window.scrollTo(0, 0);
      } else {
        body.classList.add('ios-fullscreen'); setIsIOSFullscreen(true);
        if (viewport) viewport.setAttribute('content', 'width=device-width, initial-scale=1, viewport-fit=cover, user-scalable=no');
        setTimeout(() => { window.scrollTo(0, 1); setTimeout(() => window.scrollTo(0, 0), 100); }, 100);
      }
    } else {
      if (!document.fullscreenElement) document.documentElement.requestFullscreen().catch(() => {});
      else document.exitFullscreen().catch(() => {});
    }
  };

  const handleAuthSuccess = () => { setShowAuthModal(false); setAuthModalStarMode(false); window.location.reload(); };

  const copyPaymentString = async () => {
    if (gameState.payment_string) {
      try { await navigator.clipboard.writeText(gameState.payment_string); setCopiedStatus('payment'); setTimeout(() => setCopiedStatus(''), 2000); } catch (err) {}
    }
  };

  const openTelegramBot = () => {
    window.open(gameState.telegram_bot_link || 'https://t.me/ainovel_helper_bot', '_blank');
  };

  const openTelegramGroup = () => { if (gameState.telegram_group_link) window.open(gameState.telegram_group_link, '_blank'); };

  const copyVerificationCode = async () => {
    if (gameState.telegram_verification_code) {
      try { await navigator.clipboard.writeText(gameState.telegram_verification_code); setCopiedStatus('verification'); setTimeout(() => setCopiedStatus(''), 2000); } catch (err) {}
    }
  };

  const fetchAiModels = async () => {
    try { const response = await apiGet(API_URLS.AI_MODELS); setAiModels(response.data.models || []); } catch (err) {}
  };

  const AI_PREFERENCE_MAX = 1500;

  const openAiPreference = async () => {
    setAiPreferenceOpen(true);
    try {
      const response = await apiGet(API_URLS.USER_ME);
      setAiPreference(response.data?.ai_preference_instruction || '');
    } catch (err) {}
  };

  const saveAiPreference = async () => {
    setAiPreferenceSaving(true);
    try {
      await apiPost(API_URLS.USER_AI_PREFERENCE, { ai_preference_instruction: aiPreference });
      showToast(t('Пожелания сохранены'), 'success');
      setAiPreferenceOpen(false);
    } catch (err) {
      showToast(getSanitizedErrorMessage(err), 'error');
    }
    setAiPreferenceSaving(false);
  };

  const OWN_LORE_MAX = 15000;

  const openOwnLore = async () => {
    if (!gameState?.novel_id) return;
    // Nothing is editable until the saved lore arrives — saving a stale or empty form would wipe it
    setOwnLore(''); setOwnLoreLoading(true); setOwnLoreOpen(true);
    try {
      const response = await apiGet(API_URLS.USER_NOVEL_GAME_OWN_LORE(gameState.novel_id));
      setOwnLore(response.data?.own_lore || '');
    } catch (err) {
      showToast(getSanitizedErrorMessage(err), 'error');
      setOwnLoreOpen(false);
    }
    setOwnLoreLoading(false);
  };

  const saveOwnLore = async () => {
    setOwnLoreSaving(true);
    try {
      await apiPut(API_URLS.USER_NOVEL_GAME_OWN_LORE(gameState.novel_id), { own_lore: ownLore });
      showToast(t('Лор сохранён'), 'success');
      setOwnLoreOpen(false);
    } catch (err) {
      showToast(getSanitizedErrorMessage(err), 'error');
    }
    setOwnLoreSaving(false);
  };

  const applyAiModel = (modelId) => {
    setSelectedAiModel(modelId);
    localStorage.setItem('playpage-selected-ai-model', modelId);
    setAiModelSelectorOpen(false);
  };

  const handleAiModelSelection = (modelId) => {
    // Test models need an explicit confirmation first
    if (aiModels.find(m => m.id === modelId)?.is_test) {
      setPendingTestModel(modelId);
      return;
    }
    applyAiModel(modelId);
  };

  // Image-generator catalogue: prices and copy come from the backend so the quoted price is
  // always the one the open-world turn actually charges.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await apiGet(API_URLS.IMAGE_MODELS);
        const data = response.data || response;
        if (!cancelled && data.models && data.models.length) setImageModels(data.models);
      } catch (err) { /* keep the fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const imageModelOption = (id) => imageModels.find((m) => m.id === id) || imageModels[0];
  const imageModelDesc = (id) => imageModelOption(id).description || '';
  const imageModelStars = (id) => imageModelOption(id).stars;

  const fetchGameState = async () => {
    setLoading(true);
    try {
      const response = await apiGet(API_URLS.GAME_CONTINUE(playLink));
      const gameStateData = response.data || response;
      processMusic(gameStateData.music);
      setGameState(gameStateData);
      updateSpritesWithFade(gameStateData.sprites);
      if (gameStateData.ow_image_generation_enabled !== undefined) {
        setOwImageGenEnabled(gameStateData.ow_image_generation_enabled);
      }
      if (gameStateData.ow_image_style) {
        setOwImageStyle(gameStateData.ow_image_style);
      }
      if (gameStateData.ow_image_model) {
        setOwImageModel(gameStateData.ow_image_model);
      }
      setLoading(false);
    } catch (err) {
      if ([403, 404].includes(err?.status) || [403, 404].includes(err?.response?.status)) { setFatalError(true); setLoading(false); return; }
      showToast(getSanitizedErrorMessage(err), 'error'); setLoading(false);
    }
  };

  // Grow the message box with its text (typed, picked from suggestions or cleared after sending)
  // up to 120px, then scroll inside it.
  useLayoutEffect(() => {
    const el = messageInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
    el.style.overflowY = el.scrollHeight > 120 ? 'auto' : 'hidden';
  }, [userMessage]);

  // A suggestion only fits the turn it was made for.
  // suggestionsTurnRef marks which turn the cached suggestions belong to.
  const suggestionsTurnRef = useRef(null);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  useEffect(() => { setSuggestions(null); setSuggestionsOpen(false); suggestionsTurnRef.current = null; }, [gameState.game_state_id]);

  const fetchSuggestions = async () => {
    if (suggestionsLoading || !gameState.game_state_id) return;
    if (suggestionsOpen) { setSuggestionsOpen(false); return; }
    // Generated once per turn: a repeat press just shows the same replies again.
    if (suggestions && suggestionsTurnRef.current === gameState.game_state_id) { setSuggestionsOpen(true); return; }
    setSuggestionsLoading(true);
    try {
      const turnId = gameState.game_state_id;
      const response = await apiGet(API_URLS.GAME_SUGGESTIONS(gameState.novel_id, turnId));
      suggestionsTurnRef.current = turnId;
      setSuggestions(response.data.suggestions || []);
      setSuggestionsOpen(true);
    } catch (err) {
      showToast(t('Не удалось получить подсказки'), 'error');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const pickSuggestion = (text) => {
    setUserMessage(text.slice(0, 1250));
    setSuggestionsOpen(false);
    const el = messageInputRef.current;
    if (el) el.focus();
  };

  const suggestButton = gameState.interact && !gameState.the_end ? (
    <button className="pp-suggest-btn" onClick={fetchSuggestions} disabled={loading || suggestionsLoading} title={t('Подсказка')} aria-label={t('Подсказка')}>
      {suggestionsLoading ? <span className="pp-send-spinner" /> : (suggestionsOpen ? <X size={18} /> : <Lightbulb size={18} />)}
    </button>
  ) : null;

  const suggestionsPopover = suggestionsOpen && suggestions && suggestions.length > 0 ? (
    <div className="pp-suggest-pop">
      <div className="pp-suggest-head">{t('Варианты ответа')}</div>
      {suggestions.map((text, i) => (
        <button key={i} className="pp-suggest-item" onClick={() => pickSuggestion(text)}>{text}</button>
      ))}
    </div>
  ) : null;

  const handleSendMessage = async () => {
    if (loading) return;
    if (gameState.the_end) return;
    if (!gameState.interact && gameState.static_endings && gameState.static_endings.length > 0) return;
    if (messageInputRef.current) messageInputRef.current.blur();
    setLoading(true);
    const previousCharacterMessage = gameState.character_message && gameState.character_message.trim() ? { message: gameState.character_message, name: gameState.character_name, character_id: gameState.character_id } : null;
    const currentUserMessage = userMessage.trim() || '...';
    try {
      const isInteractive = gameState.interact;
      if (isInteractive) {
        const streamUrl = API_URLS.GAME_INTERACT_STREAM(gameState.novel_id, gameState.game_state_id);
        const token = getToken();
        const anonToken = getAnonToken();
        const MAX_RETRIES = 3;
        let response;
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            response = await fetch(streamUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(token ? { 'X-Auth-Token': token } : (anonToken ? { 'X-Anon-Token': anonToken } : {})) },
              body: JSON.stringify({ text: currentUserMessage, ai_model_id: selectedAiModel, reset_dialogue: resetDialogue, extended_summary: extendedSummary })
            });
            break;
          } catch (err) {
            if (attempt === MAX_RETRIES) throw err;
          }
        }
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ detail: response.statusText }));
          throw { status: response.status, message: errorData.detail || response.statusText, detail: errorData.detail || response.statusText };
        }
        if (previousCharacterMessage) {
          const speakingCharacterSprite = gameState.sprites && gameState.sprites.find(sprite => sprite.character_id === previousCharacterMessage.character_id) || null;
          setMessageHistory(prev => [...prev.slice(-50), { id: 'CHARACTER', name: previousCharacterMessage.name, message: previousCharacterMessage.message, character_id: previousCharacterMessage.character_id, sprite: speakingCharacterSprite, displayInfo: { flags: gameState.flags, scales: gameState.scales, sprites: gameState.sprites, background: gameState.background, music: gameState.music } }]);
          }
        setMessageHistory(prev => [...prev.slice(-50), { id: 'USER', name: userCharacterName || t('Вы'), message: currentUserMessage, displayInfo: { flags: gameState.flags, scales: gameState.scales, sprites: gameState.sprites, background: gameState.background, music: gameState.music } }]);
        setUserMessage(''); if (messageInputRef.current) messageInputRef.current.style.height = 'auto';
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let firstChunkReceived = false;
        let doneReceived = false;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split('\n\n');
          buffer = events.pop();
          for (const event of events) {
            if (!event.trim()) continue;
            const dataMatch = event.match(/^data: (.+)$/m);
            if (!dataMatch) continue;
            try {
              const eventData = JSON.parse(dataMatch[1]);
              if (eventData.type === 'chunk') {
                if (!firstChunkReceived) {
                  firstChunkReceived = true; setMsgChunkIndex(0);
                  if (chatMode && chatMessagesRef.current) {
                    const msgs = chatMessagesRef.current.querySelectorAll('.pp-chat-msg--user');
                    const lastUserMsg = msgs[msgs.length - 1];
                    if (lastUserMsg) {
                      const container = chatMessagesRef.current;
                      const targetScroll = lastUserMsg.offsetTop - container.clientHeight / 3;
                      container.scrollTop = Math.max(0, targetScroll);
                    }
                  }
                }
                setGameState(prev => ({ ...prev, character_message: eventData.char_text || prev.character_message, character_name: eventData.char_name || prev.character_name, character_id: eventData.char_id || prev.character_id }));
              } else if (eventData.type === 'done') {
                doneReceived = true;
                processMusic(eventData.data.music);
                setGameState(eventData.data);
                updateSpritesWithFade(eventData.data.sprites);
              } else if (eventData.type === 'csam_blocked') {
                setCsamBlockedModal({ open: true, counter: eventData.counter || 5 });
                return;
              } else if (eventData.type === 'error') {
                setGameState(prev => ({
                  ...prev,
                  character_message: previousCharacterMessage ? previousCharacterMessage.message : prev.character_message,
                  character_name: previousCharacterMessage ? previousCharacterMessage.name : prev.character_name,
                  character_id: previousCharacterMessage ? previousCharacterMessage.character_id : prev.character_id,
                }));
                setMessageHistory(prev => {
                  const filtered = prev.filter(m => m !== prev[prev.length - 1] || m.id !== 'USER');
                  if (previousCharacterMessage) return filtered.slice(0, -1);
                  return filtered;
                });
                setUserMessage(currentUserMessage);
                throw new Error(eventData.message || 'Streaming error');
              }
            } catch (parseErr) {
              throw parseErr;
            }
          }
        }
        // Only a finished turn consumed the reload marker; a dropped stream must resend it
        if (doneReceived) setResetDialogue(false);
        if (audioRef && musicPlaying) audioRef.play().catch(() => {});
        if (typeof window.ym === 'function') {
          if (gameState.novel_id === '6988d18b49579faf18249176') window.ym(METRIKA_ID, 'reachGoal', 'play_hogwarts');
          if (gameState.novel_id === '69b6cd3bc9ab94373560cf9b') window.ym(METRIKA_ID, 'reachGoal', 'play_open_world');
          if (gameState.novel_id === '691c9e122ed6e13544492230') window.ym(METRIKA_ID, 'reachGoal', 'play_rassvet');
          window.ym(METRIKA_ID, 'reachGoal', gameState.paid_user ? 'paid_user_turn' : 'free_user_turn');
          window.ym(METRIKA_ID, 'reachGoal', localStorage.getItem('token') ? 'registered_turn' : 'anonymous_turn');
        }
        // GA4 targets, only for a finished turn: every message sent, and the 10th message of a session.
        if (doneReceived) {
          trackEvent('message_sent', { novel_id: gameState.novel_id });
          trackSessionMessage();
        }
      } else {
        const response = await apiPost(API_URLS.GAME_INTERACT(gameState.novel_id, gameState.game_state_id), { text: null, ai_model_id: selectedAiModel });
        const gameStateData = response.data || response;
        setMsgChunkIndex(0);
        processMusic(gameStateData.music);
        setGameState(gameStateData);
        updateSpritesWithFade(gameStateData.sprites);
        if (previousCharacterMessage) {
          const speakingCharacterSprite = gameState.sprites && gameState.sprites.find(sprite => sprite.character_id === previousCharacterMessage.character_id) || null;
          setMessageHistory(prev => [...prev.slice(-50), { id: 'CHARACTER', name: previousCharacterMessage.name, message: previousCharacterMessage.message, character_id: previousCharacterMessage.character_id, sprite: speakingCharacterSprite, displayInfo: { flags: gameState.flags, scales: gameState.scales, sprites: gameState.sprites, background: gameState.background, music: gameState.music } }]);
        }
        setMessageHistory(prev => [...prev.slice(-50), { id: 'USER', name: userCharacterName || t('Вы'), message: currentUserMessage, displayInfo: { flags: gameState.flags, scales: gameState.scales, sprites: gameState.sprites, background: gameState.background, music: gameState.music } }]);
        setUserMessage(''); if (messageInputRef.current) messageInputRef.current.style.height = 'auto';
        if (audioRef && musicPlaying) audioRef.play().catch(() => {});
      }
    } catch (err) {
      const errorText = err.message || err.detail || '';
      if (errorText.includes('csam_blocked')) {
        const counter = parseInt(errorText.split(':')[1] || '5', 10);
        setCsamBlockedModal({ open: true, counter });
        return;
      }
      if (errorText.includes("You don't own this game state")) { window.location.reload(); return; }
      if (handleStarBalanceError(errorText, err.response?.data)) { return; }
      else if (err.status === 429 || err.message?.includes('Daily interaction limit exceeded')) {
        const hoursLeft = getHoursUntilNextDay();
        showToast(t('Дневной лимит взаимодействий для этой история исчерпан. Возвращайтесь завтра!\n\nВы можете вернуться через {0} часов, чтобы продолжить эту историю.', hoursLeft), 'error');
      } else { showToast(getSanitizedErrorMessage(err), 'error'); }
    } finally { setLoading(false); }
  };
  handleSendMessageRef.current = handleSendMessage;

  const handleStaticEndingChoice = async (staticEndingId) => {
    if (loading) return;
    setLoading(true);
    if (choiceClickCount < 3) {
      const next = choiceClickCount + 1;
      setChoiceClickCount(next);
      localStorage.setItem('pp-choice-clicks', next);
    }
    const previousCharacterMessage = gameState.character_message && gameState.character_message.trim() ? { message: gameState.character_message, name: gameState.character_name, character_id: gameState.character_id } : null;
    try {
      const response = await apiPost(API_URLS.GAME_INTERACT(gameState.novel_id, gameState.game_state_id), { static_ending_id_choice: staticEndingId, ai_model_id: selectedAiModel });
      const gameStateData = response.data || response;
      setMsgChunkIndex(0);
      processMusic(gameStateData.music);
      setGameState(gameStateData);
      updateSpritesWithFade(gameStateData.sprites);
      if (previousCharacterMessage) {
        const speakingCharacterSprite = gameState.sprites && gameState.sprites.find(sprite => sprite.character_id === previousCharacterMessage.character_id) || null;
        setMessageHistory(prev => [...prev.slice(-50), { id: 'CHARACTER', name: previousCharacterMessage.name, message: previousCharacterMessage.message, character_id: previousCharacterMessage.character_id, sprite: speakingCharacterSprite, displayInfo: { flags: gameState.flags, scales: gameState.scales, sprites: gameState.sprites, background: gameState.background, music: gameState.music } }]);
      }
      const chosenEnding = gameState.static_endings.find(ending => ending.id === staticEndingId);
      if (chosenEnding) {
        setMessageHistory(prev => [...prev.slice(-50), { id: 'USER', name: userCharacterName || t('Вы'), message: `[Choice: ${chosenEnding.name}]`, displayInfo: { flags: gameState.flags, scales: gameState.scales, sprites: gameState.sprites, background: gameState.background, music: gameState.music } }]);
      }
      if (audioRef && musicPlaying) audioRef.play().catch(() => {});
    } catch (err) {
      const errorText = err.message || err.detail || '';
      if (errorText.includes('csam_blocked')) {
        const counter = parseInt(errorText.split(':')[1] || '5', 10);
        setCsamBlockedModal({ open: true, counter });
        return;
      }
      if (errorText.includes("You don't own this game state")) { window.location.reload(); return; }
      if (handleStarBalanceError(errorText, err.response?.data)) { return; }
      else if (err.status === 429 || err.message?.includes('Daily interaction limit exceeded')) {
        const hoursLeft = getHoursUntilNextDay();
        showToast(t('Дневной лимит взаимодействий для этой истории исчерпан. Возвращайтесь завтра!\n\nВы можете вернуться через {0} часов, чтобы продолжить эту историю.', hoursLeft), 'error');
      } else { showToast(getSanitizedErrorMessage(err), 'error'); }
    } finally { setLoading(false); }
  };

  const handleSubmitBiography = async () => {
    if (!userCharacterName.trim() || loading) return;
    if (gameState.required_18_age && !ageVerified) return;
    setLoading(true);
    try {
      await apiPut(API_URLS.USER_NOVEL_GAME_BIOGRAPHY(gameState.novel_id), { prompt_name: userCharacterName, prompt_biography: userCharacterBiography });
      fetchGameState();
    } catch (err) { showToast(getSanitizedErrorMessage(err), 'error'); setLoading(false); }
  };

  const openHistoryModal = async () => {
    if (!gameState || !gameState.game_state_id) return;
    setHistoryModalOpen(true); setLoadingHistory(true);
    try { const response = await apiGet(API_URLS.GAME_HISTORY(gameState.novel_id, gameState.game_state_id)); const data = response.data; const messages = Array.isArray(data) ? data : (data.messages || []); const hasMore = Array.isArray(data) ? false : (data.has_more || false); setGameHistory(messages); setHistoryHasMore(hasMore); }
    catch (error) { console.error('History fetch error:', error); }
    finally { setLoadingHistory(false); }
  };


  const openSavesModal = async () => { if (!gameState || !gameState.novel_id) return; setSavesModalOpen(true); fetchSaves(); };
  const closeSavesModal = () => { setSavesModalOpen(false); setNewSaveName(''); };

  const openReportModal = () => { setReportModalOpen(true); };

  const openOwInfoModal = async () => {
    if (!gameState || !gameState.novel_id || !gameState.game_state_id) return;
    setOwInfoModalOpen(true);
    setOwInfoLoading(true);
    try {
      const response = await apiGet(API_URLS.GAME_OW_INFO(gameState.novel_id, gameState.game_state_id));
      setOwInfo(response.data || response);
    } catch (err) { setOwInfo(null); }
    finally { setOwInfoLoading(false); }
  };

  const saveOwImageStyle = async (enabled, style, model) => {
    if (!gameState || !gameState.game_state_id) return;
    setOwSavingStyle(true);
    try {
      await apiPut(API_URLS.GAME_OW_IMAGE_GENERATION(gameState.novel_id, gameState.game_state_id), { image_generation_enabled: enabled, image_style: style, image_model: model });
      setOwImageGenEnabled(enabled);
      setOwImageStyle(style);
      setOwImageModel(model);
      setGameState(prev => ({ ...prev, ow_image_generation_enabled: enabled, ow_image_style: style, ow_image_model: model }));
      setOwInfo(prev => prev ? { ...prev, image_generation_enabled: enabled, image_style: style, image_model: model } : prev);
      showToast(t('Настройки изображений сохранены'), 'success');
    } catch (err) { showToast(getSanitizedErrorMessage(err), 'error'); }
    finally { setOwSavingStyle(false); }
  };

  const fetchSaves = async () => {
    if (!gameState || !gameState.novel_id) return;
    setLoadingSaves(true);
    try { const response = await apiGet(API_URLS.GAME_SAVES(gameState.novel_id)); setGameSaves([...(response.data || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''))); }
    catch (error) {}
    finally { setLoadingSaves(false); }
  };

  const handleCreateSave = async (e) => {
    e.preventDefault();
    if (!newSaveName.trim() || !gameState || !gameState.game_state_id) return;
    setLoadingSaves(true);
    try { await apiPost(API_URLS.GAME_CREATE_SAVE(gameState.novel_id, gameState.game_state_id), { name: newSaveName.trim() }); setNewSaveName(''); fetchSaves(); }
    catch (error) {}
    finally { setLoadingSaves(false); }
  };

  const handleDeleteSave = async (saveId) => {
    if (!gameState || !gameState.novel_id) return;
    setLoadingSaves(true);
    try { await apiDelete(API_URLS.GAME_DELETE_SAVE(gameState.novel_id, saveId)); fetchSaves(); }
    catch (error) {}
    finally { setLoadingSaves(false); }
  };

  const handleLoadSave = async (gameStateId) => {
    if (!gameState || !gameState.novel_id) return;
    setLoadingSaves(true); setSavesModalOpen(false);
    try {
      const response = await apiGet(API_URLS.GAME_HISTORY_ROLLBACK(gameState.novel_id, gameStateId));
      processMusic(response.data.music);
      setGameState(response.data); updateSpritesWithFade(response.data.sprites); setUserMessage(''); if (messageInputRef.current) messageInputRef.current.style.height = 'auto';
      setMessageHistory([]);
      if (response.data.is_open_world && response.data.current_messages_count === 0) { setOwDisclaimerShown(false); if (response.data.ow_image_generation_enabled !== undefined) setOwImageGenEnabled(response.data.ow_image_generation_enabled); if (response.data.ow_image_style) setOwImageStyle(response.data.ow_image_style); if (response.data.ow_image_model) setOwImageModel(response.data.ow_image_model); }
    } catch (error) { showToast(getSanitizedErrorMessage(error), 'error'); }
    finally { setLoadingSaves(false); }
  };

  const handleRollback = async (gameStateId) => {
    if (!gameState || !gameState.novel_id) return;
    setLoading(true);
    try {
      const response = await apiGet(API_URLS.GAME_HISTORY_ROLLBACK(gameState.novel_id, gameStateId));
      if (response && response.data) {
        processMusic(response.data.music);
        setGameState(response.data); updateSpritesWithFade(response.data.sprites); setHistoryModalOpen(false);
        setMessageHistory([]);
        if (response.data.is_open_world && response.data.current_messages_count === 0) { setOwDisclaimerShown(false); if (response.data.ow_image_generation_enabled !== undefined) setOwImageGenEnabled(response.data.ow_image_generation_enabled); if (response.data.ow_image_style) setOwImageStyle(response.data.ow_image_style); if (response.data.ow_image_model) setOwImageModel(response.data.ow_image_model); }
      }
    } catch (err) { showToast(getSanitizedErrorMessage(err), 'error'); }
    finally { setLoading(false); }
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } };

  // ===== BIOGRAPHY SCREEN =====
  if (gameState.biography_needed) {
    return (
      <div className="pp-bio-wrap">
        <ParticleBackground />
        <div className="pp-bio-form">
          <a href={localePath('/search')} className="pp-bio-back">{t('← Выйти')}</a>
          <h2>{t('Настройка вашего персонажа')}</h2>


          <div className="pp-bio-group">
            <label>{t('Имя вашего персонажа')}</label>
            <input
              type="text"
              className="pp-bio-input"
              value={userCharacterName}
              onChange={(e) => setUserCharacterName(e.target.value.slice(0, 100))}
              placeholder={t('Имя вашего персонажа')}
              disabled={loading}
            />
          </div>

          <div className="pp-bio-group">
            <label>{t('Ваш пол')}</label>
            <div className="pp-bio-gender">
              <label
                onClick={() => {
                  if (!loading) {
                    setUserGender('male');
                    const genderText = 'Пол: мужской. Парень';
                    updateBiography(genderText, customBiography);
                  }
                }}
              >
                <div className={`pp-bio-gender-box${userGender === 'male' ? ' active' : ''}`}>
                  {userGender === 'male' ? '✓' : ''}
                </div>
                {t('Мужской')}
              </label>
              <label
                onClick={() => {
                  if (!loading) {
                    setUserGender('female');
                    const genderText = 'Пол: женский. Девушка';
                    updateBiography(genderText, customBiography);
                  }
                }}
              >
                <div className={`pp-bio-gender-box${userGender === 'female' ? ' active' : ''}`}>
                  {userGender === 'female' ? '✓' : ''}
                </div>
                {t('Женский')}
              </label>
            </div>
          </div>

          <div className="pp-bio-group">
            <button
              type="button"
              className="pp-bio-expand-btn"
              onClick={() => setShowCustomBiography(!showCustomBiography)}
              disabled={loading}
            >
              <span className={`pp-bio-expand-arrow${showCustomBiography ? ' open' : ''}`}>▶</span>
              {t('Добавить вашу биографию')}
            </button>
          </div>

          {showCustomBiography && (
            <div className="pp-bio-group">
              <label>{t('Описание персонажа')}</label>
              <textarea
                className="pp-bio-textarea"
                value={customBiography}
                onChange={(e) => {
                  const text = e.target.value;
                  if (text.length <= 2500) {
                    setCustomBiography(text);
                    updateBiography(getGenderText(), text);
                  }
                }}
                placeholder={t('Добавьте дополнительные детали персонажа (максимум 2500 символов)...')}
                disabled={loading}
                rows={4}
              />
              <div className={`pp-bio-char-count${customBiography.length > 2375 ? ' warn' : ''}`}>
                {customBiography.length}/2500
              </div>
            </div>
          )}

          {gameState.required_18_age && (
            <button
              type="button"
              className="pp-bio-age"
              onClick={() => setAgeVerified(!ageVerified)}
              disabled={loading}
            >
              <div className={`pp-bio-age-box${ageVerified ? ' checked' : ''}`}>
                {ageVerified ? '✓' : ''}
              </div>
              <span className="pp-bio-age-text">{t('Мне исполнилось 18 лет')}</span>
            </button>
          )}

          <button
            className="pp-bio-submit"
            onClick={handleSubmitBiography}
            disabled={!userCharacterName.trim() || loading || (gameState.required_18_age && !ageVerified)}
          >
            {t('Отправить')}
          </button>
        </div>
      </div>
    );
  }

  // ===== FATAL ERROR SCREEN =====
  if (fatalError) {
    return (
      <div className="pp-loading">
        <div className="pp-fatal-card">
          <p>{t('Что-то пошло не так...')}</p>
          <a href={localePath('/search')} className="pp-fatal-btn">{t('Вернуться к поиску')}</a>
        </div>
      </div>
    );
  }

  // ===== LOADING SCREEN =====
  if (loading && !gameState.game_state_id) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>{t('Загрузка...')}</p>
      </div>
    );
  }


  // ===== MAIN GAME RENDER =====
  return (
    <div className="pp-page">
      <Helmet>
        <title>{t('Играть — {0}', BRAND)}</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="pp-canvas">
        {/* Background */}
        <div className={`pp-bg${gameState.the_end ? ' ended' : ''}`} style={{ height: `${bgHeight}px`, ...(chatMode ? { filter: 'blur(8px) brightness(0.4)' } : {}) }} onClick={!chatMode ? (gameState.interact ? handleScreenClick : handleStaticScreenClick) : undefined}>
          {gameState.background && (
            <img
              src={`${GCS_BUCKET_URL}/production/${gameState.novel_id}/background/${gameState.background.id}.${gameState.background.extension}${gameState.background.cache_id ? `?cache_id=${gameState.background.cache_id}` : ''}`}
              alt=""
              className={`pp-bg-img${spritesHidden ? ' pp-bg-img--contain' : ''}`}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}
        </div>

        {/* Tap to reveal UI */}
        {uiHidden && (
          <div className="pp-reveal-overlay" onClick={() => setUiHidden(false)} />
        )}

        {/* Chat Mode Interface */}
        {chatMode && !uiHidden && (
          <div className="pp-chat-mode">
            <div className="pp-chat-header">
              <div className="pp-header-info">
                {gameState.user_star_balance !== undefined && (
                  <span className="pp-star-balance">★ {gameState.user_star_balance}</span>
                )}
              </div>
              <div className="pp-btn-grid">
                <button className="pp-mini-btn" onClick={() => setMenuModalOpen(true)}><Menu size={18} /></button>
                <button className="pp-mini-btn" onClick={() => openHistoryModal()}><ScrollText size={18} /></button>
                {gameState.is_open_world && <button className="pp-mini-btn" title={t('Мир: персонажи и локации')} onClick={() => openOwInfoModal()}><Info size={18} /></button>}
                <button className="pp-mini-btn" title={spritesHidden ? t('Показать персонажей') : t('Скрыть персонажей')} onClick={() => setSpritesHidden(v => !v)}>{spritesHidden ? <Image size={18} /> : <ImageOff size={18} />}</button>
                <button className="pp-mini-btn" onClick={() => setAiModelSelectorOpen(true)}><Bot size={18} /></button>
              </div>
            </div>
            <div className="pp-chat-messages" ref={chatMessagesRef}>
              {messageHistory.map((msg, i) => {
                const isUser = msg.id === 'USER';
                const spriteUrl = !isUser && msg.sprite ? `${GCS_BUCKET_URL}/production/${gameState.novel_id}/sprite/${msg.sprite.id}.${msg.sprite.extension}${msg.sprite.cache_id ? `?cache_id=${msg.sprite.cache_id}` : ''}` : null;
                return (
                  <div key={i} className={`pp-chat-msg${isUser ? ' pp-chat-msg--user' : ' pp-chat-msg--char'}`}>
                    <div className="pp-chat-bubble">
                      <div className="pp-chat-name-row">
                        {!isUser && spriteUrl && (
                          <div className="pp-chat-avatar">
                            <img src={spriteUrl} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                        <div className="pp-chat-name">{msg.name}</div>
                      </div>
                      <div className="pp-chat-text">{formatMessage(msg.message)}</div>
                    </div>
                  </div>
                );
              })}
              {gameState.character_message && gameState.character_message.trim() && (() => {
                const lastCharMsg = [...messageHistory].reverse().find(m => m.id === 'CHARACTER');
                if (lastCharMsg && lastCharMsg.message === gameState.character_message) return null;
                const speakerSprite = gameState.sprites && gameState.sprites.find(s => String(s.character_id) === String(gameState.character_id));
                const spriteUrl = speakerSprite ? `${GCS_BUCKET_URL}/production/${gameState.novel_id}/sprite/${speakerSprite.id}.${speakerSprite.extension}${speakerSprite.cache_id ? `?cache_id=${speakerSprite.cache_id}` : ''}` : null;
                return (
                  <div className="pp-chat-msg pp-chat-msg--char pp-chat-msg--current">
                    <div className="pp-chat-bubble">
                      <div className="pp-chat-name-row">
                        {spriteUrl && (
                          <div className="pp-chat-avatar">
                            <img src={spriteUrl} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
                          </div>
                        )}
                        <div className="pp-chat-name">{gameState.character_name || 'Character'}</div>
                      </div>
                      <div className="pp-chat-text">{formatMessage(gameState.character_message)}</div>
                    </div>
                  </div>
                );
              })()}
              {loading && !gameState.character_message && (
                <div className="pp-chat-msg pp-chat-msg--char">
                  <div className="pp-chat-bubble">
                    <span className="pp-send-spinner" />
                  </div>
                </div>
              )}
            </div>
            {(gameState.interact || gameState.static_endings?.length > 0) && !gameState.the_end && (
              <div className="pp-chat-input-row">
                {gameState.static_endings && gameState.static_endings.length > 0 && (
                  choicesHidden ? (
                    <button className="pp-choices-toggle" onClick={() => setChoicesHidden(false)}>›</button>
                  ) : (
                    <div className="pp-chat-choices">
                      <span className="pp-choices-label">{t('Выборы:')}</span>
                      {gameState.static_endings.map((ending) => (
                        <button key={ending.id} className={`pp-choice-btn${choiceClickCount < 3 ? ' pp-choice-btn--glow' : ''}`} onClick={() => handleStaticEndingChoice(ending.id)} disabled={loading}>
                          {ending.display_name}
                        </button>
                      ))}
                      <button className="pp-choices-toggle" onClick={() => setChoicesHidden(true)}>‹</button>
                    </div>
                  )
                )}
                {gameState.sprites && gameState.sprites.length > 0 && (
                  <div className="pp-avatars-row">
                    {[...gameState.sprites].sort((a, b) => (a.character_name || a.character_id).localeCompare(b.character_name || b.character_id)).map((sprite) => {
                      const isSpeaker = String(sprite.character_id) === String(gameState.character_id);
                      const showName = tappedAvatarId === sprite.character_id;
                      return (
                        <div key={sprite.character_id} className="pp-avatar-wrap" onClick={() => {
                          if (tappedAvatarTimer.current) clearTimeout(tappedAvatarTimer.current);
                          if (showName) { setTappedAvatarId(null); } else {
                            setTappedAvatarId(sprite.character_id);
                            tappedAvatarTimer.current = setTimeout(() => setTappedAvatarId(null), 3000);
                          }
                        }}>
                          {showName && sprite.character_name && (
                            <span className="pp-avatar-name">{sprite.character_name}</span>
                          )}
                          <div className={`pp-avatar${isSpeaker ? ' speaking' : ''}`}>
                            <img
                              src={`${GCS_BUCKET_URL}/production/${gameState.novel_id}/sprite/${sprite.id}.${sprite.extension}${sprite.cache_id ? `?cache_id=${sprite.cache_id}` : ''}`}
                              alt=""
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {gameState.interact && suggestionsPopover}
                {gameState.interact && (
                <div className="pp-chat-input-inner">
                  <div className="pp-input-wrap">
                  {suggestButton}
                  <textarea
                    ref={messageInputRef}
                    className="pp-msg-input"
                    value={userMessage}
                    onChange={(e) => { if (e.target.value.length <= 1250) { setUserMessage(e.target.value); } }}
                    onKeyDown={handleKeyPress}
                    placeholder={t('Введите ваше сообщение...')}
                    rows={1}
                  />
                  </div>
                  <button className="pp-send-btn" onClick={handleSendMessage} disabled={loading || gameState.payment_required || gameState.telegram_required}>
                    {loading ? <span className="pp-send-spinner" /> : '➤'}
                  </button>
                </div>
                )}
              </div>
            )}
            {!gameState.interact && !gameState.the_end && !gameState.static_endings?.length && (
              <div className="pp-chat-input-row">
                <button className="pp-send-btn" onClick={handleSendMessage} disabled={loading} style={{ width: '100%', borderRadius: '12px' }}>
                  {loading ? <span className="pp-send-spinner" /> : t('Продолжить ➤')}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Message Interface */}
        <div className={`pp-msg-ui${uiHidden ? ' pp-msg-ui--hidden' : ''}${chatMode ? ' pp-msg-ui--hidden' : ''}`}>
          <div className="pp-msg-box" ref={messageContainerRef}>
            {!gameState.the_end && !gameState.payment_required && !gameState.telegram_required && (() => {
              // Compute fresh (don't read the stale ref set later in the render) — see note at the input block below.
              const chunks = (gameState.character_message && gameState.character_message.trim())
                ? splitMessageChunks(gameState.character_message)
                : [];
              const notLastChunk = chunks.length > 1 && msgChunkIndex < chunks.length - 1;
              const showHint = !gameState.interact && (!gameState.static_endings || gameState.static_endings.length === 0) || (gameState.interact && notLastChunk);
              if (!showHint || hintHidden) return null;
              return (
                <div className="pp-hint-row" onClick={notLastChunk ? nextChunk : handleStaticScreenClick} style={{ cursor: 'pointer' }}>
                  <span className="pp-msg-hint">{t('Нажмите на экран для продолжения')}</span>
                </div>
              );
            })()}
            {/* Visual Mode - Bottom content */}
            <div className="pp-bottom-wrap">
              <div className="pp-bottom-header" ref={bottomHeaderRef}>
                  <div className="pp-header-info">
                    {gameState.user_star_balance !== undefined && (
                      <span className="pp-star-balance">★ {gameState.user_star_balance}</span>
                    )}
                    {gameState.max_messages_count && gameState.max_messages_count > 0 && (
                      <div className="pp-progress pp-progress--header">
                        <div className="pp-progress-bar">
                          <div className="pp-progress-fill" style={{ width: `${Math.min(100, (gameState.current_messages_count / gameState.max_messages_count) * 100)}%` }} />
                        </div>
                        <div className="pp-progress-text">{gameState.current_messages_count}/{gameState.max_messages_count}</div>
                      </div>
                    )}
                  </div>
                  <div className="pp-btn-grid">
                    <button className="pp-mini-btn" onClick={() => setMenuModalOpen(true)}><Menu size={18} /></button>
                    <button className="pp-mini-btn" onClick={() => openHistoryModal()}><ScrollText size={18} /></button>
                {gameState.is_open_world && <button className="pp-mini-btn" title={t('Мир: персонажи и локации')} onClick={() => openOwInfoModal()}><Info size={18} /></button>}
                    <button className="pp-mini-btn" onClick={() => setUiHidden(true)}><Eye size={18} /></button>
                    <button className="pp-mini-btn" title={spritesHidden ? t('Показать персонажей') : t('Скрыть персонажей')} onClick={() => setSpritesHidden(v => !v)}>{spritesHidden ? <Image size={18} /> : <ImageOff size={18} />}</button>
                    <button className="pp-mini-btn" onClick={() => setAiModelSelectorOpen(true)}><Bot size={18} /></button>
                  </div>
              </div>
              <div className="pp-bottom-main">
                {gameState.static_endings && gameState.static_endings.length > 0 && (
                  <div className="pp-top-row">
                    {choicesHidden ? (
                      <button className="pp-choices-toggle" onClick={() => setChoicesHidden(false)}>›</button>
                    ) : (
                      <>
                        <div className="pp-choices">
                          <span className="pp-choices-label">{t('Выборы:')}</span>
                          <div className="pp-choices-btns">
                            {gameState.static_endings.map((ending) => (
                              <button key={ending.id} className={`pp-choice-btn${choiceClickCount < 3 ? ' pp-choice-btn--glow' : ''}`} onClick={() => handleStaticEndingChoice(ending.id)} disabled={loading}>
                                {ending.display_name}
                              </button>
                            ))}
                          </div>
                        </div>
                        <button className="pp-choices-toggle" onClick={() => setChoicesHidden(true)}>‹</button>
                      </>
                    )}
                    <div className="pp-landscape-controls">
                      <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => setMenuModalOpen(true)} disabled={loading}><Menu size={16} /><span className="pp-mini-label">{t('Меню')}</span></button>
                      <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => openHistoryModal()} disabled={loading}><ScrollText size={16} /><span className="pp-mini-label">{t('История')}</span></button>
                      {gameState.is_open_world && <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => openOwInfoModal()} disabled={loading}><Info size={16} /><span className="pp-mini-label">{t('Мир')}</span></button>}
                      <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => setUiHidden(true)} disabled={loading}><Eye size={16} /><span className="pp-mini-label">{t('Скрыть')}</span></button>
                      <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => setSpritesHidden(v => !v)} disabled={loading}>{spritesHidden ? <Image size={16} /> : <ImageOff size={16} />}<span className="pp-mini-label">{spritesHidden ? t('Спрайты') : t('Скрыть')}</span></button>
                    </div>
                  </div>
                )}
                {gameState.sprites && gameState.sprites.length > maxVisibleChars && (
                  <div className="pp-avatars-row">
                    {[...gameState.sprites].sort((a, b) => (a.character_name || a.character_id).localeCompare(b.character_name || b.character_id)).map((sprite) => {
                      const isSpeaker = String(sprite.character_id) === String(gameState.character_id);
                      const showName = tappedAvatarId === sprite.character_id;
                      return (
                        <div key={sprite.character_id} className="pp-avatar-wrap" onClick={() => {
                          if (tappedAvatarTimer.current) clearTimeout(tappedAvatarTimer.current);
                          if (showName) { setTappedAvatarId(null); } else {
                            setTappedAvatarId(sprite.character_id);
                            tappedAvatarTimer.current = setTimeout(() => setTappedAvatarId(null), 3000);
                          }
                        }}>
                          {showName && sprite.character_name && (
                            <span className="pp-avatar-name">{sprite.character_name}</span>
                          )}
                          <div className={`pp-avatar${isSpeaker ? ' speaking' : ''}`}>
                            <img
                              src={`${GCS_BUCKET_URL}/production/${gameState.novel_id}/sprite/${sprite.id}.${sprite.extension}${sprite.cache_id ? `?cache_id=${sprite.cache_id}` : ''}`}
                              alt=""
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            {(() => {
              // Compute chunks up front so msgChunksRef is written BEFORE notLastChunk reads it.
              // Previously the ref was only set deeper in the JSX (below), so notLastChunk read the
              // previous render's value — a one-render lag that StrictMode's dev double-render hid but
              // production (single render) exposed, hiding the input on multi-chunk mobile messages.
              const chunks = (gameState.character_message && gameState.character_message.trim())
                ? splitMessageChunks(gameState.character_message)
                : [];
              msgChunksRef.current = chunks;
              const notLastChunk = chunks.length > 1 && msgChunkIndex < chunks.length - 1;
              const inputHidden = !gameState.interact || loading || notLastChunk;
              const showInput = gameState.interact && !loading && !notLastChunk;
              const hasContent = (gameState.character_message && gameState.character_message.trim()) || gameState.interact || loading;
              return (<>
              {hasContent && <div className={`pp-msg-bottom${loading ? ' pp-msg-bottom--loading' : ''}${inputHidden ? ' pp-msg-bottom--noinput' : ''}`}>
                <div
                  className="pp-msg-content"
                  ref={msgContentRef}
                  onTouchStart={isMobileDevice && gameState.interact ? handleChunkTouchStart : undefined}
                  onTouchMove={isMobileDevice && gameState.interact ? handleChunkTouchMove : undefined}
                  onTouchEnd={isMobileDevice && gameState.interact ? handleChunkTouchEnd : undefined}
                  onClick={handleMsgClick}
                >
                  {gameState.character_message && gameState.character_message.trim() ? (() => {
                    const kbOpen = keyboardOpen && isMobileDevice;
                    if (kbOpen) {
                      return chunks.map((chunk, i) => (
                        <div key={i} className="pp-char-msg pp-char-msg--kb-chunk">
                          {i === 0 && <span className="pp-char-name">{gameState.character_name || 'Character'}: </span>}
                          <span className="pp-char-text">{formatMessage(i < chunks.length - 1 ? chunk + '...' : chunk)}</span>
                        </div>
                      ));
                    }
                    const currentIdx = Math.min(msgChunkIndex, chunks.length - 1);
                    const hasMore = currentIdx < chunks.length - 1 || loading;
                    const chunk = (chunks[currentIdx] || '') + (hasMore ? '...' : '');
                    return (
                      <div className="pp-char-msg pp-char-msg--chunked">
                        <div className={`pp-chunk-side pp-chunk-side--left${currentIdx === 0 ? ' pp-chunk-side--disabled' : ''}`} onClick={(e) => { e.stopPropagation(); if (currentIdx > 0) prevChunk(); }}>«</div>
                        <div className="pp-chunk-body">
                          <span className="pp-char-name">{gameState.character_name || 'Character'}: </span>
                          <span
                            key={msgChunkIndex}
                            className={`pp-char-text${!chunkDragging && chunkEnterDir ? ` pp-chunk-enter-${chunkEnterDir}` : ''}`}
                            style={chunkDragging ? {
                              transform: `translateX(${chunkDragX}px)`,
                              opacity: Math.max(0, 1 - Math.abs(chunkDragX) / 200),
                            } : undefined}
                          >{formatMessage(chunk)}</span>
                        </div>
                        <div className={`pp-chunk-side pp-chunk-side--right${!loading && currentIdx >= chunks.length - 1 ? ' pp-chunk-side--disabled' : ''}`} onClick={(e) => { e.stopPropagation(); if (!loading && currentIdx < chunks.length - 1) nextChunk(); }}>
                          {loading && currentIdx >= chunks.length - 1 ? <span className="pp-send-spinner" /> : '»'}
                        </div>
                      </div>
                    );
                  })() : (
                    loading ? (
                      <div className="pp-char-msg" style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
                        <span className="pp-send-spinner" />
                      </div>
                    ) : gameState.interact ? (
                      <div className="pp-char-msg">
                        <span className="pp-char-text">{t('Скажи что-нибудь первым!')}</span>
                      </div>
                    ) : null
                  )}
                </div>
              </div>}
              {(() => {
                if (!showInput) return null;
                return (<>
                  {suggestionsPopover}
                  <div className={`pp-input-row${safariFocused ? ' pp-input-row--safari-focused' : ''}`}>
                    <div className="pp-input-wrap">
                      {suggestButton}
                      <textarea ref={messageInputRef} className="pp-msg-input" value={userMessage} onChange={(e) => { if (e.target.value.length <= 1250) { setUserMessage(e.target.value); } }} onKeyDown={handleKeyPress} placeholder={t('Введите ваше сообщение...')} rows={1} onFocus={() => { if (isSafari() && document.fullscreenElement) setSafariFocused(true); }} onBlur={() => { if (isSafari()) setSafariFocused(false); }} />
                      <div className="pp-inline-controls">
                        <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => setMenuModalOpen(true)}><Menu size={16} /><span className="pp-mini-label">{t('Меню')}</span></button>
                        <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => openHistoryModal()}><ScrollText size={16} /><span className="pp-mini-label">{t('История')}</span></button>
                        {gameState.is_open_world && <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => openOwInfoModal()}><Info size={16} /><span className="pp-mini-label">{t('Мир')}</span></button>}
                        <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => setUiHidden(true)}><Eye size={16} /><span className="pp-mini-label">{t('Скрыть')}</span></button>
                        <button className="pp-mini-btn pp-mini-btn--labeled" onClick={() => setSpritesHidden(v => !v)}>{spritesHidden ? <Image size={16} /> : <ImageOff size={16} />}<span className="pp-mini-label">{spritesHidden ? t('Спрайты') : t('Скрыть')}</span></button>
                      </div>
                    </div>
                    {!gameState.the_end && (
                      <button className="pp-send-btn" onClick={handleSendMessage} disabled={loading || gameState.payment_required || gameState.telegram_required}>
                        {loading ? <span className="pp-send-spinner" /> : '➤'}
                      </button>
                    )}
                  </div>
                </>);
              })()}
              </>);
            })()}
              </div>
            </div>
          </div>
        </div>

        {/* Character Sprites */}
        {(() => {
          const isMobile = Math.min(screenWidth, bgHeight) <= 768;
          const isPortrait = bgHeight > screenWidth;
          const isLandscape = !isPortrait;
          let spriteHeight;
          let spriteHeightPx;
          if (isMobile && isLandscape) {
            spriteHeight = '98vh';
            spriteHeightPx = (98 / 100) * bgHeight;
          } else {
            const maxVh = isMobile ? (isPortrait ? 80 : 75) : 85;
            spriteHeightPx = (maxVh / 100) * bgHeight;
            spriteHeight = `${spriteHeightPx}px`;
          }

          let visibleSprites = displaySprites && displaySprites.length > 0
            ? [...displaySprites].sort((a, b) => a.character_id.localeCompare(b.character_id))
            : [];

          if (isMobile && isPortrait && visibleSprites.length > maxVisibleChars) {
            if (maxVisibleChars === 1) {
              const speaker = visibleSprites.find(s => String(s.character_id) === String(gameState.character_id));
              visibleSprites = speaker ? [speaker] : visibleSprites.slice(0, 1);
            } else {
              const currentSpeaker = String(gameState.character_id);
              if (currentSpeaker !== prevSpriteIdsRef.current) {
                const previousSpeaker = prevSpriteIdsRef.current;
                const nonSpeakers = visibleSprites.filter(s => String(s.character_id) !== currentSpeaker);
                const prevSpeakerSprite = nonSpeakers.find(s => String(s.character_id) === previousSpeaker);
                randomNonSpeakerRef.current = prevSpeakerSprite
                  ? prevSpeakerSprite.character_id
                  : (nonSpeakers.length > 0 ? nonSpeakers[Math.floor(Math.random() * nonSpeakers.length)].character_id : null);
                prevSpriteIdsRef.current = currentSpeaker;
              }
              const speaker = visibleSprites.find(s => String(s.character_id) === String(gameState.character_id));
              const secondSprite = randomNonSpeakerRef.current && String(randomNonSpeakerRef.current) !== String(gameState.character_id)
                ? visibleSprites.find(s => s.character_id === randomNonSpeakerRef.current)
                : null;
              visibleSprites = speaker ? [speaker, secondSprite].filter(Boolean) : visibleSprites.slice(0, 2);
              visibleSprites.sort((a, b) => a.character_id.localeCompare(b.character_id));
            }
          }

          return (
            <div
              className={`pp-sprites${visibleSprites.length >= 3 ? ' multi' : ''}`}
              style={{
                bottom: 0,
                height: spriteHeight,
                opacity: chatMode ? 0 : spritesHidden ? 0 : spriteHeightPx < 200 ? 0 : spritesFading ? 0 : (keyboardOpen && isMobileDevice) ? 0 : 1,
                transition: 'opacity 0.4s ease',
                pointerEvents: 'none'
              }}
            >
              {visibleSprites.length > 0 ? (
                visibleSprites.map((sprite, index) => {
                  const totalSprites = visibleSprites.length;
                  // const spriteWidthPx = totalSprites === 1 ? screenWidth : 1000;
                  // const screenW = totalSprites > 1 ? screenWidth + 100 : screenWidth;
                  const spriteWidthPx = 1000;
                  const screenW = screenWidth;
                  const centerX = totalSprites === 1
                    ? screenW / 2
                    : (screenW / (totalSprites + 1)) * (index + 1);
                  const leftPx = centerX - spriteWidthPx / 2;
                  const isSpeaker = String(sprite.character_id) === String(gameState.character_id);
                  return (
                    <div
                      key={sprite.character_id}
                      className={`pp-sprite${isSpeaker ? ' speaker' : ''}`}
                      style={{ left: `${leftPx}px`, width: `${spriteWidthPx}px`, height: '100%', zIndex: isSpeaker ? 1000 : 10 + totalSprites - index }}
                    >
                      <img
                        src={`${GCS_BUCKET_URL}/production/${gameState.novel_id}/sprite/${sprite.id}.${sprite.extension}${sprite.cache_id ? `?cache_id=${sprite.cache_id}` : ''}`}
                        alt=""
                        style={{ height: '100%', width: 'auto', position: 'absolute', bottom: '0', left: '50%', transform: 'translateX(-50%)' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  );
                })
              ) : null}
            </div>
          );
        })()}

        {/* Open World Disclaimer */}
        {gameState.is_open_world && gameState.current_messages_count === 0 && !owDisclaimerShown && !gameState.the_end && (
          <div className="pp-end-overlay">
            <div className="pp-end-card">
              <div className="pp-end-msg">
                {t('Это сцена открытого мира! Вы управляете сюжетом.')}<br /><br />
                {t('Описывайте новых персонажей прямо в сообщении, например: «К нам подходит загадочная девушка в красном платье».')}<br />
                {t('Меняйте локацию: «Мы идём в заброшенный замок».')}<br /><br />
                {t('Каждая генерация изображения стоит {0} ⭐ (изображение генерируется только при создании нового персонажа или локации).', imageModelStars(owImageModel))}<br />
                {t('Если вы отключите генерацию изображений, рекомендуем использовать режим чата. Включите его в меню.')}
              </div>
              {/* One flex child of .pp-end-card, so the card's 20px gap applies once around the
                  whole block instead of between every row inside it. */}
              <div className="pp-ow-settings">
                <button
                  type="button"
                  className="pp-bio-age"
                  onClick={() => setOwImageGenEnabled(!owImageGenEnabled)}
                >
                  <div className={`pp-bio-age-box${owImageGenEnabled ? ' checked' : ''}`}>
                    {owImageGenEnabled ? '✓' : ''}
                  </div>
                  <span className="pp-bio-age-text">{t('Генерация изображений ({0} ⭐ за изображение)', imageModelStars(owImageModel))}</span>
                </button>
                {owImageGenEnabled && (
                  <>
                    <div className="pp-ow-pick-title">{t('🎨 Стиль изображений')}</div>
                    <div className="pp-bio-gender">
                      <label onClick={() => setOwImageStyle('anime')}>
                        <div className={`pp-bio-gender-box${owImageStyle === 'anime' ? ' active' : ''}`}>
                          {owImageStyle === 'anime' ? '✓' : ''}
                        </div>
                        {t('Аниме')}
                      </label>
                      <label onClick={() => setOwImageStyle('realistic')}>
                        <div className={`pp-bio-gender-box${owImageStyle === 'realistic' ? ' active' : ''}`}>
                          {owImageStyle === 'realistic' ? '✓' : ''}
                        </div>
                        {t('Реалистичный')}
                      </label>
                    </div>
                    <div className="pp-ow-pick-title">{t('🤖 Модель генерации')}</div>
                    <div className="pp-bio-gender">
                      {imageModels.map((m) => (
                        <label key={m.id} onClick={() => setOwImageModel(m.id)}>
                          <div className={`pp-bio-gender-box${owImageModel === m.id ? ' active' : ''}`}>
                            {owImageModel === m.id ? '✓' : ''}
                          </div>
                          {m.name}
                        </label>
                      ))}
                    </div>
                    <div className="pp-ow-pick-desc">{imageModelDesc(owImageModel)}</div>
                  </>
                )}
              </div>
              <button className="pp-end-home" onClick={async () => {
                if (gameState.game_state_id) {
                  try {
                    await apiPut(API_URLS.GAME_OW_IMAGE_GENERATION(gameState.novel_id, gameState.game_state_id), { image_generation_enabled: owImageGenEnabled, image_style: owImageStyle, image_model: owImageModel });
                  } catch (err) {}
                }
                setOwDisclaimerShown(true);
              }} style={{ cursor: 'pointer', border: 'none' }}>
                {t('Понятно')}
              </button>
            </div>
          </div>
        )}

        {/* The End Overlay */}
        {gameState.the_end && !endOverlayClosed && (
          <div className="pp-end-overlay">
            <div className="pp-end-card">
              <button className="pp-end-close" onClick={() => setEndOverlayClosed(true)}>✕</button>
              {/* The chat and the support bot are both the Russian-language Telegram community,
                  which europe has no equivalent of — inviting a Plotex player to "our chat" would
                  point at nothing. There the thank-you stands on its own. */}
              <div className="pp-end-msg">
                {HAS_TELEGRAM
                  ? t('Спасибо, что прошли новеллу. Делитесь впечатлениями в нашем чате - нам очень важна обратная связь.')
                  : t('Спасибо, что прошли новеллу!')}
                {HAS_TELEGRAM && (
                  <>
                    <br />
                    {t('Поддержать проект можно через Telegram бота @ainovel_helper_bot.')}
                  </>
                )}
              </div>
              <a href={localePath('/')} className="pp-end-home">{t('Вернуться на главную')}</a>
            </div>
          </div>
        )}

        {/* Hidden Audio Player */}
        {currentMusic && (
          <audio
            key="music-player"
            ref={handleAudioRef}
            onEnded={handleTrackEnd}
            style={{ display: 'none' }}
            src={`${GCS_BUCKET_URL}/production/${gameState.novel_id}/music/${currentMusic.id}.${currentMusic.extension}${currentMusic.cache_id ? `?cache_id=${currentMusic.cache_id}` : ''}`}
          />
        )}


        {/* Telegram Banner */}
        {HAS_TELEGRAM && gameState.telegram_required && (
          <div className="pp-tg-banner">
            <strong>{t('Требуется подписка на Telegram')}</strong>
            <span>{t('Присоединитесь к нашей Telegram группе, затем перейдите к {0} и вставьте этот код верификации. Если вы уже в группе, просто вставьте код боту.', gameState.telegram_bot_name || '@ainovel_helper_bot')}</span>
            <span>{t('Обновите страницу после подтверждения подписки')}</span>
            <div className="pp-tg-actions">
              {gameState.telegram_group_link && (
                <div className="pp-tg-row">
                  <input type="text" className="pp-tg-input" value={gameState.telegram_group_link} readOnly />
                  <button className="pp-tg-btn" onClick={openTelegramGroup}>{t('Присоединиться к группе')}</button>
                </div>
              )}
              <div className="pp-tg-row">
                <input type="text" className="pp-tg-input" value={gameState.telegram_verification_code || ''} readOnly />
                <button className="pp-tg-copy-btn" onClick={copyVerificationCode}>📋</button>
                {copiedStatus === 'verification' && <span className="pp-copied">{t('Скопировано!')}</span>}
              </div>
              <div className="pp-tg-row">
                <input type="text" className="pp-tg-input" value={gameState.telegram_bot_link || ''} readOnly />
                <button className="pp-tg-btn" onClick={openTelegramBot}>{t('Открыть Telegram бота')}</button>
              </div>
              <button className="pp-tg-btn green" onClick={() => window.location.reload()}>{t('Обновить')}</button>
            </div>
          </div>
        )}

        {/* Payment Banner */}
        {gameState.payment_required && (
          <div className="pp-pay-banner">
            <strong>{gameState.payment_string ? t('Требуется оплата') : t('Требуется вход')}</strong>
            {gameState.payment_price && gameState.payment_price > 0 && (
              <span className="pp-pay-price">
                {t('Цена: {0} Telegram Stars или {1} рублей через банк', gameState.payment_price, Math.round(gameState.payment_price * 1.7))}
              </span>
            )}
            <span>{gameState.payment_string ? t('Перейдите в {0}, вставьте эту строку и оплатите с помощью Stars или через денежный перевод в банк. У бота есть все необходимые инструкции. Затем вернитесь и обновите страницу.', gameState.telegram_bot_name || '@ainovel_helper_bot') : t('Пожалуйста, войдите или зарегистрируйтесь для покупки этого контента.')}</span>
            <div className="pp-pay-actions">
              {gameState.payment_string ? (
                <>
                  <div className="pp-pay-label">{t('Сообщение для вставки в бота:')}</div>
                  <div className="pp-pay-row">
                    <input type="text" className="pp-pay-input" value={gameState.payment_string} readOnly />
                    <button className="pp-tg-copy-btn" onClick={copyPaymentString}>{t('Скопировать')}</button>
                    {copiedStatus === 'payment' && <span className="pp-copied">{t('Скопировано!')}</span>}
                  </div>
                  <button className="pp-tg-btn" onClick={openTelegramBot}>{t('Открыть Telegram бота')}</button>
                </>
              ) : (
                <button className="pp-login-btn" onClick={() => { setAuthModalStarMode(false); setShowAuthModal(true); }}>{t('Войти')}</button>
              )}
            </div>
          </div>
        )}

        {/* Auth Modal */}
        {showAuthModal && (
          <AuthModal
            onSuccess={handleAuthSuccess}
            onClose={() => { setShowAuthModal(false); setAuthModalStarMode(false); }}
            initialMode="register"
            starBalanceMode={authModalStarMode}
            canGetRegistrationStars={gameState.can_get_registration_stars}
          />
        )}

        {/* Star Balance Modal */}
        {starBalanceModalOpen && (
          <div className="pp-modal-overlay" onClick={() => setStarBalanceModalOpen(false)}>
            <div className="pp-modal pp-star-modal" onClick={(e) => e.stopPropagation()}>
              <div className="pp-modal-head">
                <h3 className="pp-modal-title">{t('⭐ Недостаточно звёзд')}</h3>
                <button className="pp-modal-close" onClick={() => setStarBalanceModalOpen(false)}>×</button>
              </div>
              <div className="pp-star-body">
                <p>{t('Недостаточно звёзд для этого хода. Нужно больше ⭐ для взаимодействия с персонажами.')}</p>
                <p>{t('1 ход стоит минимум 10 ⭐')}{gameState.is_open_world && gameState.ow_image_generation_enabled ? t(' + {0} ⭐ за каждое сгенерированное изображение', imageModelStars(owImageModel)) : ''}</p>
                <div className="pp-star-actions">
                  {HAS_PAYMENTS && (
                    <>
                      <a href={localePath('/payment')} className="pp-star-account" onClick={() => setStarBalanceModalOpen(false)}>
                        {t('Пополнить звёзды в Моём аккаунте')}
                      </a>
                      <a href={localePath('/payment')} className="pp-star-ad-hint" onClick={() => setStarBalanceModalOpen(false)}>
                        {t('📺 Или смотрите рекламу и получайте бесплатные звёзды')}
                      </a>
                    </>
                  )}
                  <button className="pp-star-cancel" onClick={() => setStarBalanceModalOpen(false)}>{t('Отмена')}</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CSAM Blocked Modal */}
      {csamBlockedModal.open && (
        <div className="pp-modal-overlay" onClick={() => setCsamBlockedModal({ open: false, counter: 0 })}>
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('Доступ ограничен — CSAM')}</h3>
              <button className="pp-modal-close" onClick={() => setCsamBlockedModal({ open: false, counter: 0 })}>×</button>
            </div>
            <div className="pp-modal-body" style={{ padding: '20px', lineHeight: 1.6 }}>
              <p>{t('Ваш запрос нарушил правила безопасности контента ({0}/5).', csamBlockedModal.counter)}</p>
              {csamBlockedModal.counter >= 5
                ? <p>{t('Вы достигли максимального количества нарушений и не можете продолжать играть в новеллы.')}</p>
                : <p>{t('При достижении 5 нарушений доступ к новеллам будет заблокирован.')}</p>
              }
              <p>{t('Если вы считаете, что это ошибка, напишите нам:')}</p>
              <p>Telegram: <strong>novelladmin</strong></p>
              <p>Email: <strong>ainovel@yandex.ru</strong></p>
            </div>
          </div>
        </div>
      )}

      {/* Rollback confirmation */}
      {rollbackConfirmId !== null && (
        <div className="pp-modal-overlay" style={{ zIndex: 2147483647 }} onClick={() => setRollbackConfirmId(null)}>
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('Вернуться к сообщению?')}</h3>
              <button className="pp-modal-close" onClick={() => setRollbackConfirmId(null)}>×</button>
            </div>
            <div className="pp-modal-body" style={{ padding: '20px', lineHeight: 1.6 }}>
              <p>{t('Прогресс после этого сообщения будет недоступен для продолжения, если вы не сделали сохранение. Вернуться к этому моменту?')}</p>
              <div className="pp-save-btns" style={{ marginTop: '16px' }}>
                <button className="pp-save-load" onClick={() => { const id = rollbackConfirmId; setRollbackConfirmId(null); handleRollback(id); }}>{t('Вернуться')}</button>
                <button className="pp-save-del" onClick={() => setRollbackConfirmId(null)}>{t('Отмена')}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Saves Modal */}
      {savesModalOpen && (
        <div className="pp-modal-overlay" onClick={closeSavesModal}>
          <div className="pp-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('Сохранения')}</h3>
              <button className="pp-modal-close" onClick={closeSavesModal}>×</button>
            </div>
            <div className="pp-modal-body">
              {loadingSaves ? (
                <div className="pp-loading" style={{ height: 'auto', padding: '30px 0' }}>
                  <div className="pp-spinner" />
                </div>
              ) : (
                <>
                  <div className="pp-saves-list">
                    {gameSaves.length > 0 ? (
                      gameSaves.map((save) => (
                        <div key={save.save_id} className="pp-save-item">
                          <div className="pp-save-name">{save.name}</div>
                          <div className="pp-save-btns">
                            <button className="pp-save-load" onClick={() => handleLoadSave(save.game_state_id)}>{t('Загрузить')}</button>
                            <button className="pp-save-del" onClick={() => handleDeleteSave(save.save_id)}>{t('Удалить')}</button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="pp-saves-empty">{t('Сохранений не найдено')}</div>
                    )}
                  </div>
                  <form className="pp-save-form" onSubmit={handleCreateSave}>
                    <input type="text" className="pp-save-input" value={newSaveName} onChange={(e) => setNewSaveName(e.target.value)} placeholder={t('Имя сохранения')} disabled={loadingSaves} />
                    <button type="submit" className="pp-save-create" disabled={!newSaveName.trim() || loadingSaves}>{t('Сохранить')}</button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyModalOpen && (
        <div className="pp-modal-overlay" onClick={() => setHistoryModalOpen(false)}>
          <div className="pp-modal pp-history-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('📜 История')}</h3>
              <button className="pp-modal-close" onClick={() => setHistoryModalOpen(false)}>✕</button>
            </div>
            <div className="pp-modal-body">
              {loadingHistory ? (
                <div className="pp-loading" style={{ height: 'auto', padding: '30px 0' }}>
                  <div className="pp-spinner" />
                </div>
              ) : gameHistory.length > 0 ? (
                <div className="pp-history-scroll-wrap">
                  <div className="pp-history-list" ref={historyMessagesRef} onScroll={(e) => {
                    const el = e.target;
                    const track = el.parentElement.querySelector('.pp-history-scrollbar-track');
                    const thumb = track && track.querySelector('.pp-history-scrollbar-thumb');
                    if (!thumb || !track) return;
                    const ratio = el.scrollTop / (el.scrollHeight - el.clientHeight);
                    const thumbH = Math.max(30, (el.clientHeight / el.scrollHeight) * track.clientHeight);
                    thumb.style.height = `${thumbH}px`;
                    thumb.style.top = `${ratio * (track.clientHeight - thumbH)}px`;
                  }}>
                    {historyHasMore && (
                      <div className="pp-history-more">...</div>
                    )}
                    {[...gameHistory].reverse().map((msg, index) => {
                      const isUserMessage = msg.speaker_type === 'user';
                      const isClickable = !isUserMessage;
                      return (
                        <div
                          key={index}
                          className={`pp-history-msg ${isUserMessage ? 'user' : 'character'}`}
                          onClick={isClickable ? () => setRollbackConfirmId(msg.game_state_id) : undefined}
                          title={isClickable ? t('Нажмите, чтобы перейти к этому сообщению') : undefined}
                        >
                          {isClickable && <div className="pp-history-click-hint">{t('Нажмите, чтобы перейти к этому сообщению')}</div>}
                          <div className="pp-history-sender">{msg.character_name || t('Вы')}</div>
                          {msg.character_message && <div className="pp-history-text">{formatMessage(msg.character_message)}</div>}
                          {msg.game_state_id === gameState.game_state_id && <div className="pp-history-current">{t('Текущее')}</div>}
                        </div>
                      );
                    })}
                  </div>
                  <div className="pp-history-scrollbar-track" onTouchStart={(e) => {
                    e.stopPropagation();
                    const track = e.currentTarget;
                    const list = track.parentElement.querySelector('.pp-history-list');
                    const rect = track.getBoundingClientRect();
                    const y = e.touches[0].clientY - rect.top;
                    const ratio = y / rect.height;
                    list.scrollTop = ratio * (list.scrollHeight - list.clientHeight);
                    const onMove = (ev) => {
                      ev.preventDefault();
                      const my = ev.touches[0].clientY - rect.top;
                      list.scrollTop = Math.max(0, Math.min(1, my / rect.height)) * (list.scrollHeight - list.clientHeight);
                    };
                    const onEnd = () => { document.removeEventListener('touchmove', onMove); document.removeEventListener('touchend', onEnd); };
                    document.addEventListener('touchmove', onMove, { passive: false });
                    document.addEventListener('touchend', onEnd);
                  }} onMouseDown={(e) => {
                    e.stopPropagation();
                    const track = e.currentTarget;
                    const list = track.parentElement.querySelector('.pp-history-list');
                    const rect = track.getBoundingClientRect();
                    const y = e.clientY - rect.top;
                    list.scrollTop = (y / rect.height) * (list.scrollHeight - list.clientHeight);
                    const onMove = (ev) => {
                      const my = ev.clientY - rect.top;
                      list.scrollTop = Math.max(0, Math.min(1, my / rect.height)) * (list.scrollHeight - list.clientHeight);
                    };
                    const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
                    document.addEventListener('mousemove', onMove);
                    document.addEventListener('mouseup', onUp);
                  }}>
                    <div className="pp-history-scrollbar-thumb" />
                  </div>
                </div>
              ) : (
                <div className="pp-saves-empty">{t('История пуста')}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Menu Modal */}
      {menuModalOpen && (
        <div className="pp-modal-overlay" onClick={() => setMenuModalOpen(false)}>
          <div className="pp-modal" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('Меню')}</h3>
              <button className="pp-modal-close" onClick={() => setMenuModalOpen(false)}>✕</button>
            </div>
            <div className="pp-modal-body">
              <div className="pp-menu-grid">
                <button className="pp-menu-btn" onClick={() => { toggleMusic(); setMenuModalOpen(false); }}>
                  {musicPlaying ? t('🔇 Остановить музыку') : t('🎵 Включить музыку')}
                </button>
                <button className="pp-menu-btn" onClick={() => { openSavesModal(); setMenuModalOpen(false); }}>
                  {t('💾 Сохранения')}
                </button>
                <button className="pp-menu-btn" onClick={() => { toggleFullscreen(); setMenuModalOpen(false); }}>
                  {(isIOS() && isIOSFullscreen) || (!isIOS() && document.fullscreenElement) ? t('🔍 Выйти из полноэкранного режима') : t('⛶ Полноэкранный режим')}
                </button>
                <button className="pp-menu-btn" onClick={() => { const next = maxVisibleChars === 2 ? 1 : 2; setMaxVisibleChars(next); localStorage.setItem('pp-max-visible-chars', next); }}>
                  {t('👥 Кол-во показывающихся персонажей: {0}', maxVisibleChars)}
                </button>
                <button className="pp-menu-btn" onClick={() => { setChatMode(!chatMode); setMenuModalOpen(false); }}>
                  {chatMode ? t('🎭 Визуальный режим') : t('💬 Режим чата')}
                </button>
                <button className="pp-menu-btn" onClick={() => { setMenuModalOpen(false); openAiPreference(); }}>
                  {t('✍️ Пожелания к ответам ИИ')}
                </button>
                <button className="pp-menu-btn" onClick={() => { setMenuModalOpen(false); openOwnLore(); }}>
                  {t('📜 Свой лор')}
                </button>
                <a href={`/library/${playLink}`} className="pp-menu-btn" style={{ textDecoration: 'none' }}>
                  {t('🔄 Новая игра / изменить биографию')}
                </a>
                <button className="pp-menu-btn" onClick={() => { setMenuModalOpen(false); openReportModal(); }}>
                  {t('🚩 Сообщить об ошибке / задать вопрос')}
                </button>
                <a href={localePath('/')} className="pp-menu-btn danger" style={{ textDecoration: 'none' }}>
                  {t('🏠 Вернуться на главную')}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Preference Modal */}
      {aiPreferenceOpen && (
        <div className="pp-modal-overlay" onClick={() => setAiPreferenceOpen(false)}>
          <div className="pp-modal" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('✍️ Пожелания к ответам ИИ')}</h3>
              <button className="pp-modal-close" onClick={() => setAiPreferenceOpen(false)}>✕</button>
            </div>
            <div className="pp-modal-body">
              <div className="pp-bio-group">
                <label>{t('Как ИИ должен писать ответы')}</label>
                <textarea
                  className="pp-bio-textarea"
                  value={aiPreference}
                  onChange={(e) => {
                    if (e.target.value.length <= AI_PREFERENCE_MAX) setAiPreference(e.target.value);
                  }}
                  placeholder={t('Например: пиши длиннее и подробнее, больше описаний окружения, меньше вопросов в конце реплик...')}
                  disabled={aiPreferenceSaving}
                  rows={6}
                />
                <div className={`pp-bio-char-count${aiPreference.length > AI_PREFERENCE_MAX - 50 ? ' warn' : ''}`}>
                  {aiPreference.length}/{AI_PREFERENCE_MAX}
                </div>
              </div>
              <button
                className="pp-menu-btn pp-menu-btn--purple"
                onClick={saveAiPreference}
                disabled={aiPreferenceSaving}
              >
                {aiPreferenceSaving ? t('Сохранение...') : t('💾 Сохранить')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Own Lore Modal */}
      {ownLoreOpen && (
        <div className="pp-modal-overlay" onClick={() => setOwnLoreOpen(false)}>
          <div className="pp-modal" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('📜 Свой лор')}</h3>
              <button className="pp-modal-close" onClick={() => setOwnLoreOpen(false)}>✕</button>
            </div>
            <div className="pp-modal-body">
              <div className="pp-bio-group">
                <label>{t('Дополнительные детали истории')}</label>
                <textarea
                  className="pp-bio-textarea"
                  value={ownLore}
                  onChange={(e) => {
                    if (e.target.value.length <= OWN_LORE_MAX) setOwnLore(e.target.value);
                  }}
                  placeholder={t('Например: у героя есть старший брат, в городе действует тайный орден магов...')}
                  disabled={ownLoreSaving || ownLoreLoading}
                  rows={10}
                />
                <div className={`pp-bio-char-count${ownLore.length > OWN_LORE_MAX - 500 ? ' warn' : ''}`}>
                  {ownLore.length}/{OWN_LORE_MAX}
                </div>
              </div>
              <button
                className="pp-menu-btn pp-menu-btn--purple"
                onClick={saveOwnLore}
                disabled={ownLoreSaving || ownLoreLoading}
              >
                {ownLoreSaving ? t('Сохранение...') : t('💾 Сохранить')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Open World Info Modal */}
      {owInfoModalOpen && (
        <div className="pp-modal-overlay" onClick={() => setOwInfoModalOpen(false)}>
          <div className="pp-modal" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('🌍 Мир')}</h3>
              <button className="pp-modal-close" onClick={() => setOwInfoModalOpen(false)}>✕</button>
            </div>
            <div className="pp-modal-body">
              {owInfoLoading ? (
                <p style={{ textAlign: 'center', opacity: 0.7 }}>{t('Загрузка...')}</p>
              ) : !owInfo ? (
                <p style={{ textAlign: 'center', opacity: 0.7 }}>{t('Не удалось загрузить информацию.')}</p>
              ) : (
                <div className="pp-ow-info">
                  <div className="pp-ow-now">
                    <div className="pp-ow-now-row">
                      <span className="pp-ow-now-label">{t('📍 Вы здесь:')}</span>
                      <span className="pp-ow-now-value">{owInfo.current_location_name || t('Неизвестно')}</span>
                    </div>
                    <div className="pp-ow-now-row">
                      <span className="pp-ow-now-label">{t('👤 В сцене:')}</span>
                      <span className="pp-ow-now-value">
                        {(owInfo.characters || []).filter(c => c.in_scene && !c.is_dead).map(c => c.name).join(', ') || t('Никого')}
                      </span>
                    </div>
                  </div>

                  <div className="pp-ow-section">
                    <h4 className="pp-ow-section-title">{t('🎨 Стиль изображений')}</h4>
                    <button
                      type="button"
                      className="pp-bio-age"
                      onClick={() => setOwInfo(prev => ({ ...prev, image_generation_enabled: !prev.image_generation_enabled }))}
                      style={{ margin: '8px 0', width: 'auto' }}
                      disabled={owSavingStyle}
                    >
                      <div className={`pp-bio-age-box${owInfo.image_generation_enabled ? ' checked' : ''}`}>
                        {owInfo.image_generation_enabled ? '✓' : ''}
                      </div>
                      <span className="pp-bio-age-text">{t('Генерация изображений ({0} ⭐ за изображение)', imageModelStars(owInfo.image_model || DEFAULT_IMAGE_MODEL))}</span>
                    </button>
                    {owInfo.image_generation_enabled && (
                      <div className="pp-bio-gender" style={{ margin: '8px 0' }}>
                        <label onClick={() => !owSavingStyle && setOwInfo(prev => ({ ...prev, image_style: 'anime' }))}>
                          <div className={`pp-bio-gender-box${owInfo.image_style === 'anime' ? ' active' : ''}`}>
                            {owInfo.image_style === 'anime' ? '✓' : ''}
                          </div>
                          {t('Аниме')}
                        </label>
                        <label onClick={() => !owSavingStyle && setOwInfo(prev => ({ ...prev, image_style: 'realistic' }))}>
                          <div className={`pp-bio-gender-box${owInfo.image_style === 'realistic' ? ' active' : ''}`}>
                            {owInfo.image_style === 'realistic' ? '✓' : ''}
                          </div>
                          {t('Реалистичный')}
                        </label>
                      </div>
                    )}
                    {owInfo.image_generation_enabled && (
                      <>
                        <h4 className="pp-ow-section-title">🤖 Модель изображений</h4>
                        <div className="pp-bio-gender" style={{ margin: '8px 0' }}>
                          {imageModels.map((m) => (
                            <label key={m.id} onClick={() => !owSavingStyle && setOwInfo(prev => ({ ...prev, image_model: m.id }))}>
                              <div className={`pp-bio-gender-box${(owInfo.image_model || DEFAULT_IMAGE_MODEL) === m.id ? ' active' : ''}`}>
                                {(owInfo.image_model || DEFAULT_IMAGE_MODEL) === m.id ? '✓' : ''}
                              </div>
                              {m.name}
                            </label>
                          ))}
                        </div>
                        <div className="pp-ow-pick-desc">{imageModelDesc(owInfo.image_model || DEFAULT_IMAGE_MODEL)}</div>
                      </>
                    )}
                    <button
                      className="pp-menu-btn pp-menu-btn--purple"
                      onClick={() => saveOwImageStyle(owInfo.image_generation_enabled, owInfo.image_style, owInfo.image_model || DEFAULT_IMAGE_MODEL)}
                      disabled={owSavingStyle}
                    >
                      {owSavingStyle ? t('Сохранение...') : t('💾 Сохранить настройки изображений')}
                    </button>
                  </div>

                  <div className="pp-ow-section">
                    <h4 className="pp-ow-section-title">{t('👥 Персонажи')}</h4>
                    {(owInfo.characters || []).length === 0 ? (
                      <p style={{ opacity: 0.6 }}>{t('Пока нет персонажей.')}</p>
                    ) : (
                      <ul className="pp-ow-list">
                        {[...owInfo.characters].sort((a, b) => (a.is_dead ? 2 : a.in_scene ? 0 : 1) - (b.is_dead ? 2 : b.in_scene ? 0 : 1)).map((c) => (
                          <li key={c.id} className={`pp-ow-item${c.is_dead ? ' dead' : ''}`}>
                            <span className="pp-ow-item-name">{c.name || t('Без имени')}</span>
                            <span className="pp-ow-tags">
                              {c.is_dead && <span className="pp-ow-tag dead">{t('💀 Мёртв')}</span>}
                              {!c.is_dead && c.in_scene && <span className="pp-ow-tag here">{t('● В сцене')}</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="pp-ow-section">
                    <h4 className="pp-ow-section-title">{t('🗺️ Локации')}</h4>
                    {(owInfo.locations || []).length === 0 ? (
                      <p style={{ opacity: 0.6 }}>{t('Пока нет локаций.')}</p>
                    ) : (
                      <ul className="pp-ow-list">
                        {[...owInfo.locations].sort((a, b) => (b.is_current ? 1 : 0) - (a.is_current ? 1 : 0)).map((l) => (
                          <li key={l.id} className={`pp-ow-item${l.is_current ? ' current' : ''}`}>
                            <span className="pp-ow-item-name">{l.name}</span>
                            {l.is_current && <span className="pp-ow-tag here">{t('📍 Здесь')}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Report Modal */}
      {reportModalOpen && (
        <ReportModal
          onClose={() => setReportModalOpen(false)}
          novelId={gameState.novel_id || null}
          gameStateId={gameState.game_state_id || null}
        />
      )}

      {/* AI Model Selection Modal */}
      {aiModelSelectorOpen && (
        <div className="pp-modal-overlay" onClick={() => setAiModelSelectorOpen(false)}>
          <div className="pp-modal" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('🤖 Выбрать ИИ Модель')}</h3>
              <button className="pp-modal-close" onClick={() => setAiModelSelectorOpen(false)}>✕</button>
            </div>
            <div className="pp-modal-body">
              <button
                className={`pp-menu-btn danger${resetDialogue ? ' pp-menu-btn--danger-active' : ''}`}
                onClick={() => setResetDialogue(!resetDialogue)}
              >
                {resetDialogue ? t('✅ Диалог перезагрузится со следующим сообщением') : t('🔄 Перезагрузить диалог')}
              </button>
              <div className="pp-reset-dialogue-note">
                {t('Используйте, когда в нескольких сообщениях подряд есть ошибки в тексте: сильные повторы, много иностранных слов или другие ошибки. ИИ частично забудет последние 20 сообщений. После перезагрузки кратко опишите в следующем сообщении, что сейчас происходит, чтобы персонажи вспомнили, что было.')}
              </div>
              <div className="pp-summary-toggle">
                <button
                  className={`pp-summary-option${!extendedSummary ? ' selected' : ''}`}
                  onClick={() => toggleExtendedSummary(false)}
                >
                  <strong>{t('📝 Базовая память')}</strong>
                  <span>{t('Цена ×1 · обычная память персонажей')}</span>
                </button>
                <button
                  className={`pp-summary-option${extendedSummary ? ' selected' : ''}`}
                  onClick={() => toggleExtendedSummary(true)}
                >
                  <strong>{t('📚 Расширенная память')}</strong>
                  <span>{t('Цена ×2 · персонажи помнят больше')}</span>
                </button>
              </div>
              <div className="pp-menu-grid">
                {aiModels.map((model) => (
                  <button
                    key={model.id}
                    className={`pp-ai-btn${model.id === selectedAiModel ? ' selected' : ''}`}
                    onClick={() => handleAiModelSelection(model.id)}
                  >
                    <div className="pp-ai-header">{getAiModelEmoji(model)} <strong>{model.pseudonim}</strong> ({model.stars * (extendedSummary ? 2 : 1)} ⭐{gameState.is_open_world && gameState.ow_image_generation_enabled ? t(' + {0} ⭐/изображение', imageModelStars(owImageModel)) : ''})</div>
                    {model.tags && model.tags.length > 0 && (
                      <div className="pp-ai-tags">
                        {model.tags.map((tag, index) => <span key={index} className="pp-ai-tag">{tag}</span>)}
                      </div>
                    )}
                    {model.description && <div className="pp-ai-desc">{model.description}</div>}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test model confirmation */}
      {pendingTestModel && (
        <div className="pp-modal-overlay" onClick={() => setPendingTestModel(null)}>
          <div className="pp-modal pp-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="pp-modal-head">
              <h3 className="pp-modal-title">{t('⚠️ Тестовая модель')}</h3>
              <button className="pp-modal-close" onClick={() => setPendingTestModel(null)}>✕</button>
            </div>
            <div className="pp-modal-body pp-confirm-body">
              <div>{t('Эта модель тестовая и может ошибаться. Если что-то не понравится — вернитесь к 💍 Принцессе или 👑 Королеве, они самые надёжные.')}</div>
              <div>{t('Также лучше сменить локацию (сделать выбор), чтобы диалог стал пустым — новую модель лучше начинать с чистого листа.')}</div>
              <div className="pp-confirm-actions">
                <button className="pp-confirm-btn cancel" onClick={() => setPendingTestModel(null)}>{t('Отмена')}</button>
                <button className="pp-confirm-btn ok" onClick={() => { applyAiModel(pendingTestModel); setPendingTestModel(null); }}>{t('Понятно, выбрать')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlayPage;
